/**
 * A match from its setup (BUILDLIST Phase 51.9): what the harness, auto-resolve
 * (runBattle) and — next — the rendered game start from. A setup is plain data
 * (serialisable), so a battle can be replayed or watched from it.
 */

import { yawFacing } from '../core/hits';
import { withLighterStrips } from '../core/lighterStrip';
import { POINT_DEFENCE, TOWED_GUNS, AIR_DEFENCE_SITE } from '../constants/action';
import { SIGHT_PERIOD_TICKS } from '../core/picture';
import { seatBuilds } from '../core/buildSeat';
import { terraceBuilds } from '../core/terraces';
import { withSettlements } from '../core/settlements';
import { mainRoutes } from '../core/planetRoutes';
import { roadsOf } from '../core/roads';
import { facingObjective } from '../core/capturePoints';
import { PLANET_ARCHETYPES } from '../constants/world';
import type { AutocannonCalibre, GunshipGun, PlanetArchetype } from '../types';
import type { WeatherKind } from '../core/weather';
import { squadsForTeam, squadsFromGroups } from '../core/squads';
import { issueSupports } from '../core/supportRole';
import { issueCrewmen } from '../core/crewman';
import { armourOf, armourCarry, armourIssueOf } from '../core/armour';
import { applyCarry, carriesGl40, favouredUnitId, issueLayouts, layoutGroups, layoutHasRocket, layoutPeople, layoutSlots } from '../core/loadout';
import { DEFAULT_FORMATION_SPACING } from '../core/formation';
import { exitsFromStart } from '../core/withdrawal';
import { initRespawnState } from '../core/respawn';
import type { MountArmament } from '../types/body';
import type { MatchInputBody, SquadOrder, LongGunKind, GroundWeaponKind, SideLayout, SquadType, ArmourIssue } from '../types';
import { dcos, dsin, dacos } from '../core/dmath';
import type { WeaponKey, BotConfig, BotParamsConfig } from '../types';
import { initialSeat, gunStockOn } from '../core/seats';
import { bodyHasSeat, structureJob, unitBodyAtCreation, structureBodyKind } from '../core/body';
import { PERSON_BODY_HEIGHT, personSetOf } from '../core/scaleProfile';
import { SUPPLY, BEACON, RELAY, WEAPONS, GUN_STOCK, GROUND_WEAPONS, DRONE_PAYLOADS, DRIVE_DIALS, CONVOY, MUNITIONS } from '../constants';
import { withBuilders, settleBuildings, structureFootprint, builderOf } from '../core/buildingScale';
import { planBases, planYards } from '../core/basePlanner';
import { structureBlastReach } from '../core/damage';
import { personScaleStructureHalfWidth, projectToTangent } from '../core/surfaceWalk';
import { structureHealth, armStructure } from '../core/body';
import { MOUNT_STORE_RANGE, MORTAR, TANK_GUN } from '../constants/action';
import { teamFogs, stepTeamFog, liveTeamFog } from '../core/teamFog';
import { vecNormalize, vecCross, vecAdd, vecScale, vecSub, vecLength, vecDot, surfaceTangentFrame } from '../math';
import type { FogState, StructureState, StructureType, Vec3 } from '../types';
import type {
  GameState, PlayerState, PlayerLoadout, PlanetSizePreset, MapLayout, GameMode, EnvironmentKey, ModeConfig,
  BotTuningOverrides, TurnFlow, MatchClock, PlanetState, AlertLevel, UnitKit, DeployRegion, AirborneCause, ScaleProfileName, PersonTuning } from '../types';
import { createGameConfig, createInitialGameState, startAimingPhase, computeTurnOrder, arcDistance } from '../core/gameState';
import { initFloraDestructionState } from '../core/floraTracking';
import { initMatchEventState } from '../core/matchEvents';
import { generateMap } from '../core/mapgen';
import { SeededRandom, hashKey } from '../core/random';
import { LONG_GUNS } from '../types/player';
import { initializeFog, updateFogForPlayer } from '../core/fog';
import { gatherOnBattlePlanet, layOutFront, layOutBoard, boardSeparation, gunsBehindTheLines, lineBehindTheTeam, flatSpotNear } from '../core/actionSetup';
import { initDeployState, autoSelectBotPlanetsForMode, applyDeployToPlayers, computeFanOutTargets } from '../core/deploy';
import { composeModeConfig } from '../core/modes';
import { botGuardTeams } from '../core/perception';
import { getModeRules } from '../core/modeRules';
import { setupModeLayers, finalizeModeDeploy } from '../core/modeSetup';
import { withStoryObjectives } from '../core/storyObjectives';
import { issueEngineers, toughenTargets } from '../core/charges';
import { dressBattleSite, dropGiantRocks } from '../core/biomes';
import { initForest } from '../core/forestHeld';
import { clearCapturePoints } from '../core/pointClearing';
import { prepareVertexCells } from '../core/vertexCells';
import { prepareSurfaceTables } from '../core/surface';
import { defaultUnitKit, resolveUnitModifiers, kitPreset, type KitPresetKey } from '../core/unitModifiers';
import { archetypesFor, withArchetypes, type ArchetypeName } from '../ai/botArchetypes';
import { assertBotParams, personalityFor, skillFor, teamBotParams, withTurnParams } from '../ai/botParams';
import { computeBotLoadout } from '../ai/bot';
import { Match } from './match';
import { withGrenadeStocks } from '../core/grenadeStock';
import { seedBaseIntel } from '../core/intelRecord';   // 69.73 step 3
import { withFobs } from '../core/fob';
import { convoysOn, withConvoys } from '../core/convoys';
import { withVehicleStocks } from '../core/vehicleStock';
import { composeForces, mockBudgets, type Doctrine, type SideForce } from '../core/forceCompose';
import { pointCrateDir } from '../core/supply';
import { withSupplyDrones } from '../core/supplyDrone';
import { withMotorPools, layPoolTerraces, packStartFleet } from '../core/motorPoolStock';
import { withRocketStocks } from '../core/rocketStock';
import { withGl40Stocks, issueGl40Whole } from '../core/gl40Stock';
import { withAirstrikes, airstrikesLeft } from '../core/airstrike';
import { withDropships, withGunships, withHelis, withShipPads } from '../core/dropship';
import { helipadOn, withHelipads } from '../core/helipad';
import { HELI_PAD } from '../constants/helicopter';
import { withStands } from '../core/stands';
import { withPods } from '../core/pods';
import { withBuildKits } from '../core/buildKits';
import { defaultOn, towedGunsOn, AIRSTRIKES_BY_DEFAULT } from '../core/defaultOn';

export interface MatchSetup {
  seed: string;
  /** Bot teams (every team is a bot team). Default 2. */
  teams?: number;
  /** Units per team. Default 1. */
  unitsPerTeam?: number;
  /** Uneven teams (Phase 52): units for each team, e.g. [1, 3] = one unit vs three. Overrides unitsPerTeam. */
  unitsByTeam?: number[];
  planets?: number;
  stars?: number;
  wormholePairs?: number;
  /** One difficulty for all, or one per team. Default 'hard'. */
  difficulty?: string | string[];
  /** One personality preset for all, or one per team. Default 'balanced'. */
  personality?: string | string[];
  planetSize?: PlanetSizePreset;
  mapLayout?: MapLayout;
  hazardDensity?: 'none' | 'some' | 'many';
  /** Starting CBP per unit; also the per-unit loadout budget. Default 12. */
  cbp?: number;
  /** Stop after this many rounds (turnNumber). Default 40. */
  maxRounds?: number;
  botFog?: boolean;
  friendlyFire?: number;
  /** Mode preset. Default 'deathmatch'. */
  mode?: GameMode;
  /** Environment layers stacked on the preset. */
  modifiers?: EnvironmentKey[];
  /** Turn limit override (the preset's when undefined). */
  turnLimit?: number;
  /** Ruin density (objectives with posts, vaults and claims want ruins). Default 'none' for deathmatch, 'some' otherwise. */
  ruinDensity?: 'none' | 'few' | 'some' | 'many';
  /** Landing-damage scale (match setting). Default 1. */
  fallDamageMultiplier?: number;
  /** Stabilizers on: movement spent lowers max power / preview (core/stabilizers.ts). */
  stabilizers?: boolean;
  /** Blast knockback scale (match setting). Default 1. */
  knockbackMultiplier?: number;
  /** Raw mode-config fields applied last (blackout start turn, captures required, ...). */
  modeOverrides?: Partial<ModeConfig>;
  /** Per-cause landing damage on top of fallDamageMultiplier (jump / knockback / hazard; 1 = default). */
  fallDamageByCause?: Partial<Record<AirborneCause, number>>;
  /** Tuning overrides applied to every bot (e.g. a jump profile from JUMP_PROFILES). */
  botTuning?: BotTuningOverrides;
  /** Turn flow (match setting). Default 'perUnit'; 'batched' plays the same as perUnit for bots. */
  turnFlow?: TurnFlow;
  /** Phase 59: 'action' runs the match in real time (every unit acts each tick); default 'rounds'. */
  /** Phase 59.5: an action battle's time limit in seconds (default 180) — then the mode's own end rule decides. */
  actionTimeLimit?: number;
  clock?: MatchClock;
  /** WORKBOARD I2: drones every unit carries (default none). */
  dronesPerUnit?: number;
  /** 62.3: drones each team brought (the sector's squads'), dealt one at a time over the team's units in id order. */
  dronesByTeam?: number[];
  /** 65.15: the grenades each unit carries — HE and smoke — for every team or per team. Default none. */
  grenades?: { he?: number; smoke?: number } | Array<{ he?: number; smoke?: number } | undefined>;
  /** 65.19: drone payloads on — the explosive charges each unit carries for its drones (the scanner is free), for every team or per team; crates stock drones and charges. Absent = off (65.16's kit choice). */
  chargesPerUnit?: number | number[];
  /** 65.16: the kit choice — the drones carried are strike drones (one HE charge) instead of scout drones; for every team or per team. Default scout. */
  droneKind?: 'scout' | 'strike' | Array<'scout' | 'strike' | undefined>;
  /**
   * 65.13 / 65.14 (owner, Oct 10: "an smg team fight a lmg team, or a 50%rifle:50%smg against all rifle"): each team's long
   * guns as shares (e.g. `{ rifle: 1, smg: 1 }` half and half), dealt to its units by the seed; absent (or all rifle) =
   * everyone a rifle, as before. An LMG's carrier walks at its `carry` (the kit's `walkSpeedMult`).
   */
  gunsByTeam?: Array<Partial<Record<LongGunKind, number>> | undefined>;
  /** K2: the bots write what they see into their team's shared tracks, so the commander's markers and maps show the team's picture. Default off. */
  sharedSight?: boolean;
  /** 66.3: every squad moves in this formation (squads of `squadSize`, default 3, when none are set). Absent = none (today). */
  formation?: { shape: 'column' | 'wedge' | 'line'; spacing?: number };
  /** X22: the battle has exits (where each team landed; the deploy region's facing where one was given) and takes withdraw orders. Default off. */
  extraction?: boolean;
  /** X19: each team's reserve — its respawn tickets ARE these, and a wave's arrival comes in at the team's arrival region (an exit). Default off. */
  reserves?: number[];
  /** X20: standard rounds per unit each team carries (the turn clock's `loadout.ammo.standard`, today unlimited). Absent = unlimited. */
  ammoByTeam?: Array<number | undefined>;
  /** X11: call-ins — units ask the command layer for fire on a point they see; the bots ask on their turns (squads of 3 when none are set). Default off. */
  callIns?: boolean;
  /** X2 at L2: marks crossing down — for `team`, a mark (or a fire mission with `fire`) on the other side's landing (`regionOf`'s deploy facing) or the battle planet's pole. Squads of 3 when none are set. */
  marks?: Array<{ team: number; fire: boolean; regionOf?: number }>;
  /** WORKBOARD §13 / S3: under a person layout, an explicit front this many person-heights wide (default: the classic deploy, shrunk by k). */
  frontHeights?: number;
  /**
   * 69.6a, the Foothold board (decisions 144, 145): the two bases this share of the battle world's circumference apart, (0, ½]
   * (core/actionSetup.ts layOutBoard). Each base has its HQ, landing pad and supply dump, owned; the side's lane (X22) is
   * there and its waves land there, never beside a point. Wins over `frontHeights`. Default off (today's layout).
   */
  baseSeparation?: number;
  /** WORKBOARD S1: the scale profile ('classic' default). */
  scale?: ScaleProfileName;
  /** 62.18: planets have weather (sight, perception, hearing). On by default on the action clock (decision 147; `false` turns it off); off on the turn clock. */
  weather?: boolean;
  /** X6 at L2: the sector's weather window — one kind held for the whole battle (turns `weather` on). */
  weatherFixed?: WeatherKind;
  /** Decision 120 / 62.52: wind on both clocks — shells, and on the action clock bullets, rockets, grenades, smoke and drones (turns `weather` on). On by default on the action clock (decision 147; `false` turns it off, as does `weather: false`); off on the turn clock. */
  wind?: boolean;
  /** 62.54 step 5: weather carried by the wind field's cells — fronts that move, fog that pools in low ground (turns `weather` on). Default off. */
  weatherCells?: boolean;
  /** 67.41: the battle planet's kind (the Lab's Planet row) — every planet the map makes takes it. Absent: a one-planet
   * battle on the action clock draws its kind from the seed across every archetype (`autoArchetype`); otherwise the star-driven pick. */
  planetArchetype?: PlanetArchetype;
  /** 67.41's follow-up: 'stars' keeps the star-driven pick where a seed would draw the kind (the range: a calibration rig on a stable world). */
  planetPick?: 'seed' | 'stars';
  /** 63.4's future, partial destruction step 3: a damaged tree loses its limbs, then its crown, and the limbs fall as debris (a light hit, decision 141). Default off. */
  partialDestruction?: boolean;
  /** 63.12: GameConfig.vehicleZones (vehicle damage by zone). 69.39: on by default on the action clock; the value is passed as given. */
  vehicleZones?: boolean;
  /** 63.19 step 1: GameConfig.vehicleImpact — a moving vehicle striking a person does damage (core/vehicleImpact.ts). Default off. */
  vehicleImpact?: boolean;
  /** 63.14 step 2: GameConfig.modularBuildings — the team buildings drawn from the kit by their layouts (the sim's box unchanged). Default off. */
  modularBuildings?: boolean;
  /** 72.3 step 3: GameConfig.collapse — with modular buildings, what loses its support comes down (core/collapse.ts). Default off. */
  collapse?: boolean;
  /** 63.14 2b: GameConfig.terraces — a terrace laid under each base whose footprint's rise asks for one (core/terraces.ts). Default off. */
  terraces?: boolean;
  /** 62.60: GameConfig.sightMarch (the sight march alone at a fine scale). Default off. */
  sightMarch?: boolean;
  /** 63.14 2b's dial: GameConfig.terraceRisePeople — the least rise under a footing worth a terrace (people); absent = 1.5. */
  terraceRisePeople?: number;
  /** 63.14 step 6's start: GameConfig.settlements — the planet's settlements laid as neutral houses (core/settlements.ts). Default off. */
  settlements?: boolean;
  /** §0.11 step B (3): GameConfig.regionDressing — the dressing per region of interest (core/biomes.ts). Default off. */
  regionDressing?: boolean;
  /** 69.3b, the ship as a body: GameConfig.shipsDestructible (core/shipDamage.ts). Default off. */
  shipsDestructible?: boolean;
  /** E5.4 step 4: the planet forest in the sim — the cells round the units held (core/forestHeld.ts). Default on, on the action clock at a person's scale (forestOn, v104); `false` turns it off. */
  planetForest?: boolean;
  /** E5.4's density (core/forestDensity.ts): 'relief' — valleys and drainage wooded, ridges open, a dense core — the default with the forest on; 'flat' the old band. */
  forestDensity?: 'flat' | 'relief';
  /** 67.61: jungle where wanted (GameConfig.forestJungle): the share of the wooded biomes' ground in jungle, with the forest on. Default none. */
  forestJungle?: number;
  /** 67.42: the weather cells' dials (GameConfig): the segment's seconds, × the cells' radius, cells a segment. Absent = today's. */
  weatherSegmentS?: number;
  weatherCellScale?: number;
  weatherCellsPerSegment?: number;
  /** X18 step 2: the installations' jobs act (shield, comms relay, weather station, landing pad; core/installations.ts). Default off. */
  installations?: boolean;
  /** 62.2: false = nobody crouches or goes prone (the Lab's switch). Default on. */
  stances?: boolean;
  /** Phase 65: false = today's one gun, never empty (no magazines, reload or pistol; the Lab's switch). Default on. */
  groundWeapons?: boolean;
  /** 65.7: false = no bandages (the Lab's switch). Default on. */
  medical?: boolean;
  /** 65.9: supply crates near each team's first unit. Absent = SUPPLY.crates on the action clock with bandages on, none otherwise. */
  suppliesPerTeam?: number;
  /** 65.10: a crate at each capture point (it serves the point's holder and refills while held). Absent = on wherever team crates are. */
  pointSupplies?: boolean;
  /** 65.10: the rifle and the pistol keep limited spare rounds (GameConfig.ammoReserves). Default off. */
  ammoReserves?: boolean;
  /** 69.45: GameConfig.supportRole — each squad's support carries an ammunition case (with ammoReserves only). Default off; only on is passed. */
  supportRole?: boolean;
  /** Decision 128: a gun's shell stock and resupply, and the spotter's penalties (GameConfig.gunStock / gunSpotting; at a person scale on the action clock). Default on. */
  gunStock?: boolean;
  gunSpotting?: boolean;
  /** Lane B's brain-rules row: the action bots use their kit (GameConfig.botKit). Default on (the walking row). */
  botKit?: boolean | boolean[];   // 67.24: or per team
  /** 125a: beacons (GameConfig.beacons): squads of 3 when none are set, each squad's first unit carrying one. Default off. */
  beacons?: boolean;
  /** 65.24: smoke at arm's length (GameConfig.smokeSight). DECISIONS 300: on by default on the action clock (core/defaultOn.ts); the value is passed as given. */
  smokeSight?: boolean;
  /** 65.23: grenades from the side's finite stock (GameConfig.grenadeStock), sized GRENADE_STOCK.refills issues a unit. Default off. */
  grenadeStock?: boolean;
  /** 63.1n: each side's spare vehicles, this many of every vehicle it fields (GameConfig.vehicleStock); 0 / absent: off. */
  vehicleStock?: number;
  /** 63.1n with 67.75 (GameConfig.motorPool; with `vehicleStock`): the spares are frames in each side's motor pool, unpacked there on the side's standing order (core/motorPoolStock.ts). DECISIONS 300: on by default on the action clock, where a side has a stock (poolOn). */
  motorPool?: boolean;
  /** 65.29 (2): with the rocket tube on, rockets from the side's finite stock (GameConfig.rocketStock), sized ROCKET_STOCK.refills issues a unit. Default off. */
  rocketStock?: boolean;
  /** 69.4 step 6a: build kits (GameConfig.buildKits) — each side's count of every build kind, the carried relays and beacons drawn from it. Default off. */
  buildKits?: boolean;
  /** 69.4 step 6c: the ground rules (GameConfig.groundRules) — each build kind only on the ground its row allows. Default off. */
  groundRules?: boolean;
  /** 63.5 (67.25): footsteps heard by the ground (GameConfig.footstepsHeard). Default off. */
  footstepsHeard?: boolean;
  /** 69.2b: relays (GameConfig.relays): squads of 3 when none are set, each carrying RELAY.carried to plant on the build path. Default off. */
  relays?: boolean;
  /** 69.2a: objectives known roughly (GameConfig.roughStart): each side knows the capture points as circles of ROUGH.radiusPeople round a seeded offset until one of its people sees one; its own always known. Default off. */
  roughStart?: boolean;
  /** 69.50 step 1: story objectives derived from the setup (GameConfig.storyObjectives). Off by default; only on is passed. */
  storyObjectives?: boolean;
  /** 69.61 step 4: the story's carry and transmit objectives too (GameConfig.storyCarry, with storyObjectives). Off by default; only on is passed. */
  storyCarry?: boolean;
  /** 69.61 step 5: the story's escort objective too (GameConfig.storyEscort, with storyObjectives and mountedAssault). Off by default; only on is passed. */
  storyEscort?: boolean;
  /** 69.73 step 2a: each side's intel record (GameConfig.intelRecord). Off by default; only on is passed. */
  intelRecord?: boolean;
  /** 69.47 step 1: the engineer's placed charge, the story's destroy targets toughened (GameConfig.charges). Off by default; only on is passed. */
  charges?: boolean;
  /** 69.73 step 3: what each side knows of the other's base at the start (GameConfig.baseIntel, with intelRecord). Absent: nothing. */
  baseIntel?: 'known' | 'area' | 'none';
  /** 69.61 step 6 (DECISIONS 284 (a)): each board base's HQ starts with this much health (GameConfig.hqHealth). Absent: the body's own. */
  hqHealth?: number;
  /** 63.1r: a scouting technical into its circle, its squad aboard (GameConfig.technicalRole). On by default; only off is passed. */
  technicalRole?: boolean;
  /** 63.11: small craters (GameConfig.smallCraters). Off by default (the owner, Oct 13); only on is passed. */
  smallCraters?: boolean;
  /** §5.21 step 6: bursts throw by their class (GameConfig.burstPush). Off by default; only on is passed. */
  burstPush?: boolean;
  /** 69.65: GameConfig.blastModel. On by default on the action clock; the value is passed as given. */
  blastModel?: boolean;
  /** DECISIONS 200 step 4: forward arrivals by dropship sorties (GameConfig.waveShips). Off by default; only on is passed. */
  waveShips?: boolean;
  /** §5.21 step 7: bursts suppress by their class (GameConfig.burstSuppress; with `suppression` only). Off by default. */
  burstSuppress?: boolean;
  /** 69.5f: the commander's pull to the front and the table's left-behind flag (GameConfig.frontPull). On by default; only off is passed. */
  frontPull?: boolean;
  /** 181: GameConfig.attention. On by default (the owner, Oct 14); only off is passed. */
  attention?: boolean;
  /** 182 (3): GameConfig.commanderPicture. On by default (the owner, for fairness); only off is passed. */
  commanderPicture?: boolean;
  /** §0.11 step D: GameConfig.efforts — the adaptive commanders run their sides as efforts. Off by default. */
  efforts?: boolean | boolean[];
  /** 182 (2): GameConfig.contactBelief. Off by default; only on is passed. */
  contactBelief?: boolean;
  /** 62.60 lever 2: GameConfig.quietTier (the squad's quiet tier). Off by default; only on is passed (one value, or per team). */
  quietTier?: boolean | readonly boolean[];
  /** 62.60 lever 2b: GameConfig.marchTier (the quiet tier's sibling for the march). Off by default; only on is passed (one value, or per team). */
  marchTier?: boolean | readonly boolean[];
  /** 62.60 lever 2c: GameConfig.steadyTier (the steady-contact tier). Off by default; only on is passed (one value, or per team). */
  steadyTier?: boolean | readonly boolean[];
  /** 62.68: GameConfig.brainRate (the brains at 30 a second, staggered). On by default; only off is passed (one value, or per team). */
  brainRate?: boolean | readonly boolean[];
  /** 62.60, the look period in contact: GameConfig.lookPeriod in ticks (36 = 300 ms, 48 = 400 ms). Today's 24 by default; only a longer one is passed (one value, or per team). */
  lookPeriod?: number | readonly number[];
  /** 69.9: GameConfig.pointDefence — a C-RAM and a CIWS placed with each base on the board. DECISIONS 300: on by default on the action clock (core/defaultOn.ts); the value is passed as given. */
  pointDefence?: boolean;
  /** 69.50 step 2a: GameConfig.airDefenceSites — a forward air-defence site for each base on the board. Default off; only on is passed. */
  airDefenceSites?: boolean;
  /** 69.50 step 2b (DECISIONS 265 (2)): GameConfig.siteRounds — with the sites, their missiles spent on artillery and mortar rounds too. Default off; only on is passed. */
  siteRounds?: boolean;
  /** 69.58 (DECISIONS 272 (1)): GameConfig.countermeasures — the ships' decoy flares. DECISIONS 300: on by default on the action clock (core/defaultOn.ts); the value is passed as given. */
  countermeasures?: boolean;
  /** 69.58 (DECISIONS 271 (7)): GameConfig.shipTurrets — the gunship's roof point-defence turret. DECISIONS 300: on by default on the action clock (core/defaultOn.ts); the value is passed as given. */
  shipTurrets?: boolean;
  /**
   * 69.50 step 2b (DECISIONS 265 (3c)): an operation's own sites — with airDefenceSites on, each side's sites stand where this
   * names them (a side named with none gets none) instead of the one derived from the setup; each with its generator by it.
   */
  airDefenceSitesAt?: Array<{ team: number; planetIndex: number; surfaceDir: Vec3 }>;
  /** 69.13 (a): GameConfig.towedGuns — three towed guns placed deployed behind each base on the board, with the point defence (DECISIONS 233). DECISIONS 300: on by default on the action clock (core/defaultOn.ts); the value is passed as given. */
  towedGuns?: boolean;
  /** 69.10: GameConfig.lighters — lighters from orbit to each side's strip behind its motor pool. Default off; only on is passed. */
  lighters?: boolean;
  /**
   * 69.19: each side's start fleet unwrapped at t = 0 (GameConfig.startUnwrapped): a share of each kind (0 … 1) or 'auto' (by the
   * match's roles), one for both sides or [team 0's, team 1's]; the rest packed in the side's motor pool. Absent: all unwrapped.
   */
  startUnwrapped?: number | 'auto' | Array<number | 'auto'>;
  /** 182 (1): GameConfig.pictureDecay. Off by default; only on is passed. */
  pictureDecay?: boolean;
  /** 66.13: hold what's taken (GameConfig.holdAndPush). On by default; only off is passed (one value, or per team). */
  holdAndPush?: boolean | readonly boolean[];
  /** 66.15: the standoff manoeuvre (GameConfig.standoffManoeuvre). Off by default; only on is passed (one value, or per team). */
  standoffManoeuvre?: boolean | readonly boolean[];
  /** 66.16: the flank that takes the point (GameConfig.flankTakesPoint). Off by default; only on is passed (one value, or per team). */
  flankTakesPoint?: boolean | readonly boolean[];
  /** 63.1t: scouts ride (GameConfig.scoutRide) — a squad the commander sends to scout a far circle takes the side's APC there. On by default; only off is passed. */
  scoutRide?: boolean;
  /** DECISIONS 162: the web's dense rule (every pair within link range), for one comparison. Default off: the sparse web. */
  denseWeb?: boolean;
  /** 69.4: forward bases (GameConfig.fobs; needs `relays`, the web) — each side may plant FOB.perSide on the build path, only where its web links them. Default off. */
  fobs?: boolean;
  /** 69.30 step 1: GameConfig.munitionsSupply (the dumps' rounds by type, a tank's rearm parked in reach). Off by default; only on is passed. */
  munitionsSupply?: boolean;
  /** 69.38 step 2a: GameConfig.letOutSlots (the APC loop's carriers each to its own let-out slot). On by default; the value is passed as given. */
  letOutSlots?: boolean;
  /** 69.4 step 5: convoys (GameConfig.convoys; needs `fobs` and `vehicleStock`; DECISIONS 166) — each side's supply trucks (CONVOY.trucks) carry the FOBs' supply from the base's stock. Default off. */
  convoys?: boolean;
  /** 69.32 (GameConfig.droneDrops): the points stand bare till taken, their crates brought by the sides' supply drones. Default off. */
  droneDrops?: boolean;
  /** 69.32: each side's supply drones with `droneDrops` on (absent: SUPPLY_DRONE.stock each). */
  supplyDronesByTeam?: number[];
  /** 69.4 step 6b: build costs (GameConfig.buildCosts; needs `convoys`) — every build pays material from the nearest store of its side in reach. Default off. */
  buildCosts?: boolean;
  /** 69.3a: drops at relays (GameConfig.drops) — a planted relay carries RELAY.drops; the side's arrivals may land at a linked one. Default off. */
  drops?: boolean;
  /** S7: every person carries a rocket tube (the Lab's switch) — or, as a list, per side (GameConfig.rocketTeams). Default off. */
  rockets?: boolean | boolean[];
  /** Decision 132: bullets meet people's measured bodies (the stance capsules), not today's sphere. Default off. */
  measuredBodies?: boolean;
  /** 65.28 (decision 161): a near round suppresses (GameConfig.suppression). DECISIONS 300: on by default on the action clock (core/defaultOn.ts); the value is passed as given. */
  suppression?: boolean;
  /** 65.28, the grip (GameConfig.suppressionGrip): `mark` (absent: today's), `beat` or `weight`. With `suppression` only. */
  suppressionGrip?: 'mark' | 'beat' | 'weight';
  /** 66.8 (decision 160): digging in (GameConfig.entrench). Default off. */
  entrench?: boolean;
  /** 63.6: foliage slows and nudges rounds (GameConfig.foliageBullets). Default off. */
  foliageBullets?: boolean;
  /** 66.7: foliage hides from sight (GameConfig.foliageSight). Default off. */
  foliageSight?: boolean;
  /** §15.2: the turn bots fire on the team picture (GameConfig.teamPicture). Default off. */
  teamPicture?: boolean;
  /** Decision 24 experiments: the handheld's lethality dials (see PersonTuning). */
  personTuning?: PersonTuning;
  /** Per-team tuning overrides on top of `botTuning` (head-to-head profile matches). */
  botTuningByTeam?: Array<BotTuningOverrides | undefined>;
  /** The hidden archetypes (the owner's Oct 4 ask; src/ai/botArchetypes.ts): `true` = each team draws one by the seed and the mode; a list gives them per team. Absent = off (today's bots). */
  archetypes?: boolean | Array<ArchetypeName | undefined>;
  /** 62.48: catalogue values (src/ai/botParams.ts) for every team and per team — a bot-params file. Laid on top of the tuning options above; absent = today's bots. */
  botParams?: BotParamsConfig;
  /** Per team: where it may land (sector: the arrival lane → a hemisphere of the system and the planet; core/deploy.ts). */
  deployRegionByTeam?: Array<DeployRegion | undefined>;
  /** Record `computeStateChecksum` after every bot turn into `report.checksums` (the headless golden uses it). */
  traceChecksums?: boolean;
  /** Record the turn-end checksum the game's `?trace` logs (every turn advance, same format) into `report.turnTrace` — watch vs auto-resolve. */
  traceTurns?: boolean;
  /** Unit kit for every unit: a KIT_PRESETS name or a kit. Default 'standard' (today's game). */
  kit?: KitPresetKey | UnitKit;
  /** Per-team kits on top of `kit` (head-to-head). */
  kitByTeam?: Array<KitPresetKey | UnitKit | undefined>;
  /** Per-unit kits, [team][unit index within the team], on top of kitByTeam / kit ("what you brought", per unit — the sector's SquadUnit.kit). */
  kitByUnit?: Array<Array<KitPresetKey | UnitKit | undefined> | undefined>;
  /** Per-team credits per unit on top of `cbp` (the loadout budget and the units' starting CBP; the sector's supply per side). */
  cbpByTeam?: Array<number | undefined>;
  /** §12.7 S4: per team, the weapons its bots may buy (undefined = the default shop). The field-class `mortar` is bought only when named here. */
  weaponsByTeam?: Array<WeaponKey[] | undefined>;
  /** Phase 57: turn the alert layer on — every planet starts here. Undefined = no layer (today). */
  startingAlert?: AlertLevel;
  /** Phase 57: which teams are guards (perception-limited until alert). Undefined with the layer on = every team. */
  guardTeams?: number[];
  /** Phase 53: false = bots ignore their skill level's judgement profile (A/B against the pre-profile bot). Default true. */
  skillProfiles?: boolean;
  /** Sector layer: generate the map from this seed instead of `seed` (same system, different battle). */
  mapSeed?: string;
  /** Sector layer: planets as an earlier battle left them (craters, lost mass); must match the generated count. */
  planetsOverride?: PlanetState[];
  /** Sector layer: each unit's starting health, [team][unit index within the team] (1–100; missing = full). Data, so a setup can cross to another tab. */
  healthByTeam?: Array<number[] | undefined>;
  /** Sector layer: structures standing before the battle (what the holder built), owned by a team's first unit or by nobody. */
  structuresOverride?: Array<{ type: StructureType; planetIndex: number; surfaceDir: Vec3; ownerTeam: number | null }>;
  /** S6 harness: this many empty emplacements per team (where: `gunsAt`). */
  emplacementsPerTeam?: number;
  /** X31 (R12 step 5): motorcycles a short walk from each team's first unit on the action clock, alternating gas and electric unless `bikeVariant` says (the Lab's row). Default none. */
  bikesPerTeam?: number;
  bikeVariant?: 'gas' | 'electric';
  /** 63.1d: transports (the 6×6) parked by each team's first unit on the action clock at a person scale, the ramp toward it (the Lab's row). Default none. */
  transportsPerTeam?: number;
  /** 197 step 1: tracked carriers (the M113) parked after the 6×6s behind each team on the action clock at a person scale (the Lab's row). Default none. */
  trackedPerTeam?: number;
  /** 69.29: tanks parked after the tracked carriers behind each team on the action clock at a person scale (the Lab's row). Default none. */
  tanksPerTeam?: number;
  /** 69.29 (DECISIONS 245 (5)): GameConfig.tankStowage — a tank's explosive and solid rounds (each 0–40; absent: TANK_GUN.stowage's 20 and 10). */
  tankStowage?: { he: number; solid: number };
  /** 63.1u: smoke charges each vehicle carries (GameConfig.vehicleSmoke; no reload). Default none. */
  vehicleSmoke?: number;
  /** 63.1u: the bots pop the dispensers too (GameConfig.botVehicleSmoke). Off by default; only on is passed. */
  botVehicleSmoke?: boolean;
  /** 69.46: GameConfig.crewmanRole — each squad's crewman works a vehicle's seat faster (core/crewman.ts). DECISIONS 300: on by default on the action clock (core/defaultOn.ts); the value is passed as given. */
  crewmanRole?: boolean;
  /** 63.1o: technicals (a light truck, a .50 in its bed) parked with the 6×6s behind each team, after them on the same line (the Lab's row). Default none. */
  technicalsPerTeam?: number;
  /** 69.21 (DECISIONS 239): what the technicals' mounts carry, cycled over each side's technicals in id order (absent, or 'hmg': the HMG as today) — the rocket technical, the missile technical. */
  technicalArmaments?: readonly MountArmament[];
  /** 69.21 (DECISIONS 240): GameConfig.mountStore — the rounds a mounted launcher carries to reload from (2–6; absent: MOUNT_LAUNCHERS' own). */
  mountStore?: number;
  /** 69.23 (DECISIONS 239, 241): what the tracked carriers (the M113s) carry, cycled over each side's in id order — 'mortar' the mortar carrier (called-in fire, core/mortar.ts); absent, or 'hmg': the .50 as today. */
  trackedArmaments?: readonly MountArmament[];
  /** 69.23: GameConfig.mortarStore — the rounds a mortar carrier's store holds (MORTAR.storeRange; absent: MORTAR.store). */
  mortarStore?: number;
  /** 65.22: emplaced heavy machine guns per team, on the action clock at a person scale: a few people ahead of each team's first unit toward the nearest enemy (the Lab's row). Default none. */
  hmgsPerTeam?: number;
  /**
   * X18 step 2 harness hook: these structures stand round each team's first unit (a ring 2.5 u out, offset from the
   * emplacements' ring), owned by it — a shield, a landing pad, an antenna — so a locked match exercises the
   * installations' jobs. Nothing without the list.
   */
  installationsPerTeam?: StructureType[];
  /**
   * Where S6 puts a team's guns. 'lines' (decision 126, the default): at a person scale on the action clock, behind the
   * team's people by its heaviest shell's knockback reach, away from the enemy; elsewhere as 'hand'. 'hand': a short
   * walk from the team's first unit (the seat smoke, a look at a gun up close).
   */
  gunsAt?: 'lines' | 'hand';
  /** I1 harness: this many information towers per team, six person-heights from the team's first unit (owned by it). */
  towersPerTeam?: number;
  /** E5 / decision 87: false leaves a person-scale battle undressed (no biome stands) — the tree analysis's A/B. Default on; classic matches never dress. */
  biomes?: boolean;
  /** 67.12: × the stands a person-scale site takes (the Lab's stand density; default 1 = as it was). */
  standDensity?: number;
  /** 67.46: on a site wider than the Lab's, the dressing gathered into patches where the fight goes (core/biomes PATCH): on by default since 67.46 step 3 (lane F measured it inside decision 136); `false` = the dressing as it was. Trees are obstacles: a stored case that turns it on takes a version. */
  biomePatches?: boolean;
  /** E5.1 (the tree analysis, harness only): no trees at all — the giant (planet-scale) flora cleared too, after dressing. */
  noFlora?: boolean;
  /** 69.91 (a): the action clock's capture circles cleared of the rocks and flora whose walk footprint reaches in (core/pointClearing.ts). Default on; `false` = the map as it was (the props count's A/B). */
  clearPoints?: boolean;
  /** Sector layer: last touch on the ready-to-aim state (ammo and the like). */
  prepareState?: (state: GameState) => GameState;
  /** R13 (sector): props destroyed in earlier battles on this map, by generated id — they start dead (rocks, flora, hazards). */
  destroyedProps?: { rocks: number[]; flora: number[]; hazards: number[] };
  /** R11 step 2 (lane D): every team's units in squads of this size (`state.squads`); orders then arrive as `order` inputs. */
  squadSize?: number;
  /** 68.10: fluid squad membership (strays join, respawns join, ones merge; core/squadMembership.ts). Needs squads; off by default. */
  fluidSquads?: boolean;
  /**
   * 68.9: a bot-led squad's leader sets its steps (src/ai/squadLead.ts): the side it approaches from, bounds in contact, posts
   * at the place. Needs squads. Decision 148: on by default on the action clock wherever there are squads — `false` turns it
   * off; only an explicit `true` makes squads where the setup has none (so a duel stays a duel).
   */
  squadLead?: boolean | boolean[];   // (or per team)
  /** 70.3: GameConfig.coverAcross — cover read across the body. On by default on the action clock; the value is passed as given. */
  coverAcross?: boolean;
  /** 66.7: the cover system's behaviour (GameConfig.coverMoves) — first, a holding squad takes cover from the enemy's direction. On by default since 66.19 (DECISIONS 187). */
  coverMoves?: boolean | boolean[];   // (or per team)
  /** 62.43: the local steer — a walk goes to a goal a body can stand at (out of a shape's clearance), and to a goal beside an obstacle rather than round it. DECISIONS 300: on by default on the action clock (core/defaultOn.ts); the value is passed as given. */
  localSteer?: boolean;
  /** §0.11 step C: the planet's route graph for long drives and the ship's choice (GameConfig.planetRoutes). Off by default. */
  planetRoutes?: boolean;
  /** 216 (4): roads between the settlements and the bases (GameConfig.roads). Off by default. */
  roads?: boolean;
  /** DECISIONS 216: real timing — the throw's wind-up and the stances' times in the sim (GameConfig.realTiming). DECISIONS 300: on by default on the action clock (core/defaultOn.ts); the value is passed as given. */
  realTiming?: boolean;
  /** 63.15 step 1: the jump's own feel (GameConfig.jumpFeel) and its dials (with it on). DECISIONS 300: on by default on the action clock (core/defaultOn.ts); the value is passed as given. */
  jumpFeel?: boolean;
  jumpApex?: number;
  jumpGravity?: number;
  /** R11 step 2 (lane D): explicit squads — per team, groups of unit indices in the team's id order (the sector's own squads). Wins over `squadSize`. */
  squadsByTeam?: Array<number[][] | undefined>;
  /** 65.32 (DECISIONS 189): a side's squad layout — how many of each squad type it fields (core/loadout.ts). Its size is the layout's total (whole squads, the Lab's 50 at most), its squads are formed from it and each member gets its slot; the side's gun shares and rocket switch don't apply to it. Absent: as before. */
  layoutByTeam?: Array<SideLayout | undefined>;
  /** 65.32: the squad types the layouts count (absent: SQUAD_TYPES). */
  /** 65.32 (DECISIONS 189, the owner: "lmg + rocket + 40mm + pistol without letting the other teams or most bots have that"): the player's own loadout, outside the layouts and the slot rule — on `team`'s first unit by id (the unit the page hands the human) and its respawns, never another (core/loadout.ts applyCarry). Absent: none. */
  playerLoadout?: { team: number; weapons: GroundWeaponKind[]; /** 185: the player's own armour (the Lab's Your armour) */ armour?: ArmourIssue };
  /** 65.27: hit locations (GameConfig.hitLocations) and the head's and the limbs' multipliers (the Lab's dials, with it on). DECISIONS 300: on by default on the action clock (core/defaultOn.ts); the value is passed as given. */
  hitLocations?: boolean;
  hitHead?: number;
  hitLimbs?: number;
  /** 185: each side's armour (the Lab's Armour: helmets, light or heavy — light and heavy with the helmet); a slot's own and the player's over it. Absent: none. */
  armourByTeam?: Array<'none' | 'helmets' | 'heavyHelmets' | 'light' | 'heavy' | 'heavyKit' | undefined>;
  /** 65.31 (the owner: "a user inventory that's configurable"): the 40 mm's rounds a life for each person who carries one — issued at the start and at each respawn (absent: the launcher's own, one in the breech and GROUND_WEAPONS.gl40.reserve spares). */
  gl40Rounds?: number;
  /** 65.31 step 2: the 40 mm's rounds from the side's finite stock (GameConfig.gl40Stock; core/gl40Stock.ts), sized GL40_STOCK.refills issues a carrier. DECISIONS 300: on by default on the action clock (core/defaultOn.ts); the value is passed as given. */
  gl40Stock?: boolean;
  /** 68.39: each side's airstrikes for the match (its commander's; core/airstrike.ts). All 0: none, and nothing changes; DECISIONS 300: absent, AIRSTRIKES_BY_DEFAULT a side on the action clock (none on the turn clock). */
  airstrikesByTeam?: number[];
  /** 69.3b: each side's dropships for the match (core/dropship.ts). Absent or all 0: none, and nothing changes. */
  dropshipsByTeam?: number[];
  /** DECISIONS 200 step 5: each side's pod drops for the match (core/pods.ts). Absent or all 0: none. */
  podsByTeam?: number[];
  /**
   * 69.49 (DECISIONS 273): each side's force composed (core/forceCompose.ts) in place of the shared vehicle, HMG and call
   * counts — `doctrines[t]` side t's doctrine, or null for the setup's own counts (A, the authored force); `budgets[t]` its
   * budget as a factor of FORCE_BUDGET.base (equal by default). Absent: off, today's counts.
   */
  forceCompose?: { doctrines: readonly (Doctrine | null)[]; budgets?: readonly number[] };
  /** 68.39: how each side's bot commander paces them (GameConfig.airstrikeSpend; absent: 'even'). */
  airstrikeSpend?: Array<'early' | 'even' | 'late'>;
  /** 65.31 (the owner: tune the lob in play): × the 40 mm's muzzle speed, and its share of a person's felt gravity (GameConfig's; absent: GROUND_WEAPONS.gl40's). */
  gl40Speed?: number;
  gl40Gravity?: number;
  /** 65.31: the wind's share on the 40 mm's lob (GameConfig's; absent: ACTION_WIND.gl40). */
  gl40Wind?: number;
  /** 69.3b's follow-up: how a dropship leaves its landing (GameConfig.dropshipExit; absent: DROPSHIP.exit). */
  dropshipExit?: 'pivot' | 'loop';
  /** 69.37 step 1: a play ordered mounted is a mounted assault (GameConfig.mountedAssault; off by default). */
  mountedAssault?: boolean;
  /** DECISIONS 290 (1): a mounted play's column forms at an assembly point first (GameConfig.convoyAssembly; off by default). */
  convoyAssembly?: boolean;
  /** 69.18: a bot commander fetches its stranded squads home by its wave ship (GameConfig.shipPickup). DECISIONS 300: on by default on the action clock (core/defaultOn.ts); the value is passed as given. */
  shipPickup?: boolean;
  /** 69.87: GameConfig.commanderSquads — the bot commander plans over the Match's squads. On by default on the action clock; the value is passed as given. */
  commanderSquads?: boolean;
  /** 69.62 step 1: a bot commander's hot rescues — its gunship over the threat, its wave ship in under it (GameConfig.hotRescue; off by default). */
  hotRescue?: boolean;
  /** 69.52 step 1: a ship called into known air defence (GameConfig.skyRule; absent: route). */
  skyRule?: 'route' | 'refuse' | 'fly';
  /** 69.52 step 2: each side's pilots' risk (GameConfig.pilotRisk; absent: cautious). */
  pilotRisk?: number[];
  /** 69.52 step 2, 69.66: the bends' swing, people (GameConfig.shipWeave; absent: SKY_BENDS's). */
  shipWeave?: number;
  /** 63.13: each side's gunships (GameConfig.gunships; 0: none; DECISIONS 300: absent, one a side on the action clock, core/defaultOn.ts), and the side gun's calibre (GameConfig.gunshipGun; absent: the 40 mm). */
  gunships?: number;
  /** 69.59 step 1: each side's helicopters (GameConfig.helicopters; 0: none). DECISIONS 300: absent, one a side on the action clock (core/defaultOn.ts). */
  helicopters?: number;
  /** 69.59 step 3 (DECISIONS 286 (4)): GameConfig.heliSuppressor — the helicopter calls carry the IR suppressors. Default off; only on is passed. */
  heliSuppressor?: boolean;
  /** 69.74 step 2 (DECISIONS 302): GameConfig.helipad — each side's helicopters parked on a helipad behind its base, flown off it and back. Default off; only on is passed. */
  helipad?: boolean;
  gunshipGun?: GunshipGun;
  /** DECISIONS 277 (2): the chin gun's calibre (GameConfig.gunshipChin; absent: the 30 mm). */
  gunshipChin?: AutocannonCalibre;
  /** 207's follow-up: wave ships — a point within this many people of the side's pad is walked to (GameConfig.waveWalkPeople; absent: DROPSHIP.walkPeople). */
  waveWalkPeople?: number;
  /** 65.37: × the player's own fire rate (GameConfig.playerFireRate; absent: 1). */
  playerFireRate?: number;
  squadTypes?: Record<string, SquadType>;
  /** R11 step 2: inputs applied at tick 0, before anyone acts — the fidelity switch that opened an entered battle, recorded (31, 111). */
  firstInputs?: MatchInputBody[];
  /** Sector layer: the final state, so scars and survivors can travel up. */
  onFinished?: (state: GameState) => void;
  /** The Match as the harness starts it (tests read its input log). */
  onMatch?: (match: Match) => void;
}

function perTeam<T>(v: T | T[] | undefined, teams: number, def: T): T[] {
  if (v === undefined) return Array(teams).fill(def);
  if (Array.isArray(v)) return Array.from({ length: teams }, (_, i) => v[i] ?? v[v.length - 1] ?? def);
  return Array(teams).fill(v);
}

/**
 * 65.13 / 65.14: deal each team's long guns by its shares — counts by the largest remainder over its units (in id order),
 * shuffled by the seed so who carries what is not the spawn order; a rifle writes nothing (the record's default), an
 * LMG also sets the carrier's walk speed. Pure; deterministic in the seed.
 */
export function dealLongGuns(state: GameState, shares: Array<Partial<Record<LongGunKind, number>> | undefined>, seed: string): GameState {
  const deal = new Map<number, LongGunKind>();
  const teams = [...new Set(state.players.map(p => p.teamIndex))].sort((a, b) => a - b);
  for (const t of teams) {
    const w = shares[t];
    if (!w) continue;
    const ids = state.players.filter(p => p.teamIndex === t).map(p => p.id).sort((a, b) => a - b);
    const kinds = LONG_GUNS.filter(k => (w[k] ?? 0) > 0), total = kinds.reduce((s, k) => s + (w[k] ?? 0), 0);
    if (total <= 0 || ids.length === 0) continue;
    const exact = kinds.map(k => ((w[k] ?? 0) / total) * ids.length);
    const counts = exact.map(Math.floor);
    const order = exact.map((e, i) => ({ i, r: e - Math.floor(e) })).sort((a, b) => b.r - a.r || a.i - b.i);
    for (let k = 0; counts.reduce((s, c) => s + c, 0) < ids.length; k++) counts[order[k % order.length].i]++;
    const guns: LongGunKind[] = kinds.flatMap((k, i) => Array<LongGunKind>(counts[i]).fill(k));
    const rng = new SeededRandom(`${seed}-guns-${t}`);
    for (let i = guns.length - 1; i > 0; i--) { const j = rng.int(0, i); [guns[i], guns[j]] = [guns[j], guns[i]]; }
    ids.forEach((id, i) => deal.set(id, guns[i]));
  }
  return { ...state, players: state.players.map(p => {
    const g = deal.get(p.id);
    if (!g || g === 'rifle') return p;
    const carry = GROUND_WEAPONS[g].carry;
    return { ...p, primary: g, ...(carry !== undefined && p.modifiers ? { modifiers: { ...p.modifiers, walkSpeedMult: carry } } : {}) };
  }) };
}

/**
 * The match a setup describes, deployed, fogged and ready to aim: map (from the setup's
 * map seed, or the scarred planets it carries), teams of bots with their difficulty,
 * personality, tuning, kit (per unit) and supply (per team), mode layers, deploy (regions
 * by arrival), then the setup's own last word (prepareState). Pure and deterministic.
 */
/** Decision 147: wind on the action clock unless turned off (`wind: false`, or `weather: false`); on the turn clock only when asked. */
/**
 * E5.4, the planet forest on by default (v104; the owner: "some dense forests where it makes sense", then "Forest plan
 * pretty good"): on the action clock at a person's scale unless the setup says `planetForest: false` (the Lab's row,
 * `?forest=0`); the classic scale and the turn game never hold it (core/forestHeld.ts initForest). Its density by relief
 * unless the setup says `forestDensity: 'flat'`.
 */
/** Does the setup stand a motor pool: the Motor pool row with a stock, lighters (v118), or 69.19's start fleet. */
function poolOn(opts: MatchSetup, sides: readonly SideForce[]): boolean {
  const pool = opts.motorPool === true || (opts.clock === 'action' && opts.motorPool !== false);   // (300: on by default on the action clock)
  return !!(pool && opts.vehicleStock && opts.vehicleStock > 0) || !!opts.lighters || (startFleetOf(opts, sides) && packsSome(opts));
}

/**
 * 69.19: does a side keep part of its start fleet packed — a share under all, or auto (by the roles: some side always packs).
 * All for both (the harness's default) stands no pool for the start fleet: the vehicles stand pre-unwrapped, today's start
 * (the owner: "for convenience the ability to configure pre-unwrapped vehicels").
 */
function packsSome(opts: MatchSetup): boolean {
  const v = opts.startUnwrapped;
  if (v === undefined) return false;
  return (Array.isArray(v) ? v : [v]).some(x => x === 'auto' || x < 1);
}

/** 69.19: does the setup field a starting ground vehicle where a motor pool can stand (the action clock, human scale): bikes, the 6×6s, the tracked, the technicals, the convoys' truck. */
function startFleetOf(opts: MatchSetup, sides: readonly SideForce[]): boolean {
  if (opts.clock !== 'action' || opts.scale !== 'human') return false;
  return sides.some(f => f.bikes > 0 || f.transports > 0 || f.tracked > 0 || f.tanks > 0 || f.technicals > 0)
    || convoysOn({ convoys: opts.convoys, fobs: opts.fobs, relays: opts.relays, vehicleStock: opts.vehicleStock });
}

/** 68.39: each side's airstrikes — the list given, or (DECISIONS 300) AIRSTRIKES_BY_DEFAULT a side on the action clock. */
function airstrikesOf(opts: MatchSetup): readonly number[] | undefined {
  return opts.airstrikesByTeam ?? (opts.clock === 'action' ? Array.from({ length: opts.teams ?? 2 }, () => AIRSTRIKES_BY_DEFAULT) : undefined);
}

/** The setup's own counts, one side's (the shared rows, the calls' per-team lists): today's force, and the authored one (A). */
function authoredSide(opts: MatchSetup, t: number): SideForce {
  return {
    transports: Math.max(0, opts.transportsPerTeam ?? 0), tracked: Math.max(0, opts.trackedPerTeam ?? 0), trackedArmaments: opts.trackedArmaments,
    tanks: Math.max(0, opts.tanksPerTeam ?? 0), technicals: Math.max(0, opts.technicalsPerTeam ?? 0), technicalArmaments: opts.technicalArmaments,
    bikes: Math.max(0, opts.bikesPerTeam ?? 0), hmgs: Math.max(0, opts.hmgsPerTeam ?? 0),
    airstrikes: airstrikesOf(opts)?.[t] ?? 0, dropships: opts.dropshipsByTeam?.[t] ?? 0, pods: opts.podsByTeam?.[t] ?? 0,
  };
}

/** 65.32: side t's squad layout, when it has one. */
const layoutOfTeam = (opts: MatchSetup, t: number): SideLayout | undefined => (opts.layoutByTeam?.[t]?.length ? opts.layoutByTeam[t] : undefined);

/** Each side's people: its own count (uneven teams), else the setup's; a side with a squad layout is the layout's people (whole squads, 65.32). */
function unitsByTeamOf(opts: MatchSetup, teams: number): number[] {
  const units = opts.unitsByTeam && opts.unitsByTeam.length > 0
    ? Array.from({ length: teams }, (_, t) => Math.max(1, Math.floor(opts.unitsByTeam![Math.min(t, opts.unitsByTeam!.length - 1)])))
    : Array.from({ length: teams }, () => opts.unitsPerTeam ?? 1);
  for (let t = 0; t < teams; t++) { const l = layoutOfTeam(opts, t); if (l) units[t] = Math.max(1, layoutPeople(l, opts.squadTypes)); }
  return units;
}

/** 69.49: each side's force as the setup will field it (the Lab shows it under its Doctrine row). */
export function forcesOf(opts: MatchSetup): SideForce[] {
  const teams = opts.teams ?? 2;
  return sideForces(opts, teams, unitsByTeamOf(opts, teams));
}

/**
 * 69.49 (DECISIONS 273): each side's force — composed from its budget by its doctrine with `forceCompose` (a side without
 * one, and every side without the switch: the setup's own counts). A side's squads — a layout's, else its people in squads
 * of `squadSize` — cap its crews and carriers.
 */
function sideForces(opts: MatchSetup, teams: number, unitsByTeam: readonly number[]): SideForce[] {
  const squads = unitsByTeam.map((n, t) => { const l = layoutOfTeam(opts, t); return l ? l.reduce((a, q) => a + q.count, 0) : Math.ceil(n / Math.max(1, opts.squadSize ?? 3)); });
  const authored = Array.from({ length: teams }, (_, t) => authoredSide(opts, t));
  const fc = opts.forceCompose;
  if (!fc) return authored;
  const doctrines = Array.from({ length: teams }, (_, t) => fc.doctrines[t] ?? null);
  const composed = composeForces(mockBudgets(fc.budgets ?? []), doctrines, squads, opts.vehicleStock ?? 0, authored);
  return authored.map((a, t) => composed[t] ?? a);
}

function forestOn(opts: MatchSetup): boolean {
  if (opts.planetForest !== undefined) return opts.planetForest;
  return opts.clock === 'action' && opts.scale === 'human';
}
function windOn(opts: MatchSetup): boolean {
  return opts.wind === true || (opts.clock === 'action' && opts.wind !== false && opts.weather !== false);
}
/** Decision 147: weather on the action clock unless `weather: false`; on either clock when a fixed window, the wind or the cells ask for it. */
function weatherOn(opts: MatchSetup): boolean {
  return opts.weather === true || (opts.clock === 'action' && opts.weather !== false) || !!opts.weatherFixed || windOn(opts) || !!opts.weatherCells;
}
/** Decision 148: squad leaders lead on the action clock unless the setup says `squadLead: false` (an array: as it says, per team). */
function leadOn(opts: MatchSetup): boolean {
  if (opts.squadLead === false) return false;
  if (opts.squadLead !== undefined) return Array.isArray(opts.squadLead) ? opts.squadLead.some(Boolean) : opts.squadLead;
  return opts.clock === 'action';
}

/**
 * 67.41's follow-up (the owner: "random seed doesn't pick all planets, bruma is a cool ass planet to play I had never
 * played it before"): a one-planet battle on the action clock (the Lab's Auto, the menu's duel and skirmish, the harness)
 * takes its planet's kind from the seed, uniformly across every archetype. The star-driven pick gave thalia on 51 of 60
 * skirmish seeds and sonora on the rest. A map of several planets keeps the star-driven pick (one kind for all would flatten
 * it), as does the turn game. Undefined = leave it to mapgen.
 */
export function autoArchetype(opts: Pick<MatchSetup, 'clock' | 'seed' | 'mapSeed' | 'planetsOverride' | 'planetPick'>, planets: number): PlanetArchetype | undefined {
  if (opts.clock !== 'action' || planets !== 1 || opts.planetsOverride || opts.planetPick === 'stars') return undefined;
  const kinds = Object.keys(PLANET_ARCHETYPES) as PlanetArchetype[];
  const h = hashKey(`${opts.mapSeed ?? opts.seed}-planet-kind`) >>> 0;
  return kinds[h % kinds.length];
}

/** S7: the tube on for some side (`rockets` true, or a list with a true). */
/** 69.6a: the Lab ring's buildings (X18) a board base lays itself — its HQ and landing pad — left out of the ring on the board. */
const BOARD_BASE_LAYS: ReadonlySet<StructureType> = new Set<StructureType>(['ecotourism', 'landingPad']);
/** 69.6a: a side whose people all stand within this many people of its base's centre is at home (its base laid behind them). */
const BOARD_HOME_PEOPLE = 30;

const rocketsOn = (r: boolean | boolean[] | undefined): boolean => (Array.isArray(r) ? r.some(Boolean) : r === true);
/** S7 per side: the teams that carry it, from a list that is not all-true; undefined otherwise (every side, today). */
const rocketTeamsOf = (r: boolean | boolean[] | undefined): number[] | undefined =>
  Array.isArray(r) && r.some(Boolean) && !r.every(Boolean) ? r.flatMap((v, i) => (v ? [i] : [])) : undefined;

export function createMatchState(opts: MatchSetup): { state: GameState; difficulty: string[] } {
  assertBotParams(opts.botParams);
  const teams = opts.teams ?? 2;
  const unitsByTeam = unitsByTeamOf(opts, teams);
  const layoutOf = (t: number): SideLayout | undefined => layoutOfTeam(opts, t);
  const layouts = opts.layoutByTeam && Array.from({ length: teams }, (_, t) => layoutOf(t)).some(Boolean) ? Array.from({ length: teams }, (_, t) => layoutOf(t)) : undefined;
  // 69.49: each side's vehicles, HMGs and calls — the setup's counts, or composed
  const sides = sideForces(opts, teams, unitsByTeam);
  const sideOf = (t: number): SideForce => sides[t] ?? authoredSide(opts, t);
  // (its tubes: the side's rocket switch on where any member's slot carries one)
  const rocketsIn: boolean | boolean[] | undefined = layouts
    ? Array.from({ length: teams }, (_, t) => (layouts[t] ? layoutHasRocket(layouts[t]!, opts.squadTypes) : Array.isArray(opts.rockets) ? opts.rockets[t] === true : opts.rockets === true))
    : opts.rockets;
  const units = Math.max(...unitsByTeam);
  const numPlanets = opts.planets ?? 6;
  const cbp = opts.cbp ?? 12;
  const difficulty = perTeam(opts.difficulty, teams, 'hard');
  const personality = perTeam(opts.personality, teams, 'balanced');
  const total = unitsByTeam.reduce((a, b) => a + b, 0);
  // Uneven teams go through the roster-slot path (one slot per unit, each carrying its team's difficulty)
  const uneven = unitsByTeam.some(u => u !== unitsByTeam[0]);
  const unitSlots = uneven
    ? unitsByTeam.map((n, t) => Array.from({ length: n }, () => ({ type: 'bot' as const, name: '', difficulty: difficulty[t], personality: personality[t] })))
    : undefined;

  const mode = opts.mode ?? 'deathmatch';
  // the hidden archetypes: drawn once here, kept in the state; the bots play the setup's params with them under (botArchetypes.ts)
  const archetypes = archetypesFor({ seed: opts.seed, mode, teams, archetypes: opts.archetypes });
  const botParams = withArchetypes(opts.botParams, archetypes ?? undefined);
  const modeConfig = {
    ...composeModeConfig(mode, { modifiers: opts.modifiers, turnLimit: opts.turnLimit }),
    ...(opts.startingAlert ? { startingAlert: opts.startingAlert } : {}),
    ...(opts.guardTeams ? { guardTeams: opts.guardTeams } : {}),
    ...opts.modeOverrides,
  };
  const rules = getModeRules(modeConfig);
  const ruinDensity = opts.ruinDensity ?? (rules.objective === 'none' ? 'none' : 'some');

  // DECISIONS 300: the flipped switches on by default on the action clock (core/defaultOn.ts) — their dials ride on them
  const on = (v: boolean | undefined) => v === true || (opts.clock === 'action' && v !== false);
  const config = {
    ...(opts.fluidSquads ? { fluidSquads: opts.squadSize ?? 3 } : {}),   // 68.10: the squad size rides on the switch (room = it + 1)
    ...(opts.squadLead ? { squadLead: opts.squadLead } : {}),   // 68.9 (decision 148's default is set below, where there are squads)
    ...(opts.coverAcross !== undefined ? { coverAcross: opts.coverAcross } : {}),   // 70.3: on by default on the action clock, so off is passed too
    ...(opts.coverMoves === false || (Array.isArray(opts.coverMoves) && !opts.coverMoves.every(Boolean)) ? { coverMoves: opts.coverMoves } : {}),   // 66.7; 66.19: on by default, only off is passed (one value, or per team)
    ...(opts.localSteer !== undefined ? { localSteer: opts.localSteer } : {}),   // 62.43; 300: on by default on the action clock, so off is passed too
    ...(opts.planetRoutes ? { planetRoutes: true } : {}),   // §0.11 step C
    ...(opts.roads ? { roads: true } : {}),   // 216 (4)
    ...(opts.realTiming !== undefined ? { realTiming: opts.realTiming } : {}),   // 216; 300: on by default on the action clock
    ...(opts.jumpFeel !== undefined ? { jumpFeel: opts.jumpFeel } : {}),   // 300: on by default on the action clock
    ...(on(opts.jumpFeel) ? { ...(opts.jumpApex !== undefined && opts.jumpApex > 0 ? { jumpApex: opts.jumpApex } : {}), ...(opts.jumpGravity !== undefined && opts.jumpGravity > 0 ? { jumpGravity: opts.jumpGravity } : {}) } : {}),   // 63.15
    ...createGameConfig(
      total, total, numPlanets, opts.seed,
      opts.stars ?? 0, opts.wormholePairs ?? 0,
      teams, 0, teams, 1, units,
      'sequential', opts.friendlyFire ?? 0, false, opts.botFog ?? true, false, false,
      opts.hazardDensity ?? 'none', ruinDensity, 'some',
      opts.planetSize ?? 'mixed', opts.mapLayout ?? 'random', 'random', 'custom',
      modeConfig, undefined, undefined, opts.fallDamageMultiplier ?? 1.0, cbp, 1, opts.stabilizers ?? false,
    ),
    knockbackMultiplier: opts.knockbackMultiplier ?? 1.0,
    // 59.7: on the action clock a jump is a hop, not a launch — its landing is free unless the setup says otherwise
    // (knockback and other causes keep their damage)
    fallDamageByCause: opts.clock === 'action' ? { jump: 0, ...(opts.fallDamageByCause ?? {}) } : opts.fallDamageByCause,
    turnFlow: opts.turnFlow ?? 'perUnit',
    ...(opts.clock ? { clock: opts.clock } : {}),
    ...(opts.scale ? { scale: opts.scale } : {}),
    // decision 147: weather and wind on by default on the action clock (the Lab, the menu skirmish and duel; `false` turns
    // either off, and no weather means no wind); the parked turn game keeps them off unless asked
    ...(weatherOn(opts) ? { weather: true } : {}),
    ...(opts.weatherFixed ? { weatherFixed: opts.weatherFixed } : {}),
    ...(windOn(opts) ? { wind: true } : {}),
    ...(opts.weatherCells ? { weatherCells: true } : {}),
    ...(opts.partialDestruction ? { partialDestruction: true } : {}),
    ...(opts.vehicleZones !== undefined ? { vehicleZones: opts.vehicleZones } : {}),   // 63.12; 69.39: on by default on the action clock (core/vehicleZones.ts zonesOn), so off is passed too
    ...(opts.vehicleImpact ? { vehicleImpact: true } : {}),   // 63.19 step 1
    ...(opts.modularBuildings ? { modularBuildings: true } : {}),   // 63.14 step 2
    ...(opts.collapse ? { collapse: true } : {}),   // 72.3 step 3
    ...(opts.terraces ? { terraces: true } : {}),   // 63.14 2b
    ...(opts.sightMarch ? { sightMarch: true } : {}),   // 62.60: the sight march alone
    ...(opts.terraceRisePeople !== undefined ? { terraceRisePeople: opts.terraceRisePeople } : {}),   // 63.14 2b's dial
    ...(opts.settlements ? { settlements: true } : {}),   // 63.14 step 6
    ...(opts.regionDressing ? { regionDressing: true } : {}),   // §0.11 step B (3)
    ...(opts.shipsDestructible ? { shipsDestructible: true } : {}),   // 69.3b, the ship as a body
    ...(forestOn(opts) ? { planetForest: true, ...((opts.forestDensity ?? 'relief') === 'relief' ? { forestDensity: 'relief' as const } : {}), ...((opts.forestJungle ?? 0) > 0 ? { forestJungle: opts.forestJungle } : {}) } : {}),
    ...(opts.weatherSegmentS !== undefined ? { weatherSegmentS: opts.weatherSegmentS } : {}),
    ...(opts.weatherCellScale !== undefined ? { weatherCellScale: opts.weatherCellScale } : {}),
    ...(opts.weatherCellsPerSegment !== undefined ? { weatherCellsPerSegment: opts.weatherCellsPerSegment } : {}),
    ...(opts.installations ? { installations: true } : {}),
    ...(opts.callIns ? { callIns: true } : {}),
    ...(opts.stances === false ? { stances: false } : {}),
    ...(opts.groundWeapons === false ? { groundWeapons: false } : {}),
    ...(opts.medical === false ? { medical: false } : {}),
    ...(opts.ammoReserves ? { ammoReserves: true } : {}),
    ...(opts.ammoReserves && opts.supportRole ? { supportRole: true } : {}),   // 69.45: with the reserves only
    ...(opts.gunStock === false ? { gunStock: false } : {}),
    ...(opts.gunSpotting === false ? { gunSpotting: false } : {}),
    ...(opts.botKit === false ? { botKit: false } : Array.isArray(opts.botKit) && opts.botKit.some(k => k === false) ? { botKit: opts.botKit } : {}),
    ...(opts.beacons ? { beacons: true } : {}),
    ...(opts.smokeSight !== undefined ? { smokeSight: opts.smokeSight } : {}),   // 300: on by default on the action clock
    ...(opts.grenadeStock ? { grenadeStock: true } : {}),
    ...(opts.vehicleStock && opts.vehicleStock > 0 ? { vehicleStock: Math.floor(opts.vehicleStock) } : {}),
    // 63.1n with 67.75: the pool needs the stock; 69.14's fix-forward: lighters stand it too — they fly to a strip behind it and
    // carry their frames into it, and since 69.14 a pool is drawn only where it stands (the owner: "the hesco barriers and
    // barbed wire and gate and stuff became invisible"); without a stock it stands empty, the lighters on a dry run
    // 69.19 (DECISIONS 237: "no magic starting ground vehicles"): and any starting ground vehicle on the action clock at human
    // scale stands it — the start fleet comes out of it (its unwrapped share standing as before, the rest packed: packStartFleet)
    ...(poolOn(opts, sides) ? { motorPool: true } : {}),
    ...(opts.startUnwrapped !== undefined && poolOn(opts, sides) ? { startUnwrapped: Array.isArray(opts.startUnwrapped) ? [...opts.startUnwrapped] : [opts.startUnwrapped, opts.startUnwrapped] } : {}),   // 69.19
    ...(opts.rocketStock && rocketsOn(rocketsIn) ? { rocketStock: true } : {}),
    ...(opts.gl40Stock !== undefined ? { gl40Stock: opts.gl40Stock } : {}),   // 65.31 step 2; 300: on by default on the action clock
    ...(opts.hitLocations !== undefined ? { hitLocations: opts.hitLocations } : {}),   // 65.27; 300: on by default on the action clock
    ...(on(opts.hitLocations) && opts.hitHead !== undefined && opts.hitHead > 0 ? { hitHead: opts.hitHead } : {}),
    ...(on(opts.hitLocations) && opts.hitLimbs !== undefined && opts.hitLimbs > 0 ? { hitLimbs: opts.hitLimbs } : {}),
    ...(opts.airstrikeSpend?.some(x => x !== 'even') ? { airstrikeSpend: [...opts.airstrikeSpend] } : {}),   // 68.39
    ...(opts.gl40Speed !== undefined && opts.gl40Speed > 0 ? { gl40Speed: opts.gl40Speed } : {}),   // 65.31: the lob's Lab dials
    ...(opts.gl40Gravity !== undefined && opts.gl40Gravity > 0 ? { gl40Gravity: opts.gl40Gravity } : {}),
    ...(opts.gl40Wind !== undefined && opts.gl40Wind >= 0 ? { gl40Wind: opts.gl40Wind } : {}),   // 65.31: the lob's wind share
    ...(opts.playerFireRate !== undefined && opts.playerFireRate > 0 && opts.playerFireRate !== 1 ? { playerFireRate: opts.playerFireRate } : {}),   // 65.37
    ...(opts.dropshipExit === 'loop' ? { dropshipExit: 'loop' as const } : {}),   // 69.3b's follow-up
    ...(opts.skyRule && opts.skyRule !== 'route' ? { skyRule: opts.skyRule } : {}),   // 69.52 step 1: route by default
    ...(opts.mountedAssault ? { mountedAssault: true } : {}),   // 69.37 step 1: off by default
    ...(opts.convoyAssembly ? { convoyAssembly: true } : {}),   // DECISIONS 290 (1): off by default
    ...(opts.shipPickup !== undefined ? { shipPickup: opts.shipPickup } : {}),   // 69.18; 300: on by default on the action clock
    ...(opts.commanderSquads !== undefined ? { commanderSquads: opts.commanderSquads } : {}),   // 69.87: on by default on the action clock, so off is passed too
    ...(opts.hotRescue ? { hotRescue: true } : {}),   // 69.62: off by default
    ...(opts.pilotRisk ? { pilotRisk: [...opts.pilotRisk] } : {}),   // 69.52 step 2
    ...(opts.shipWeave !== undefined ? { shipWeave: opts.shipWeave } : {}),   // 69.52 step 2
    ...(opts.gunships !== undefined ? { gunships: Math.max(0, Math.floor(opts.gunships)) } : {}),   // 63.13; 300: one a side by default on the action clock (core/defaultOn.ts), so 0 is passed too
    ...(opts.helicopters !== undefined ? { helicopters: Math.max(0, Math.floor(opts.helicopters)) } : {}),   // 69.59 step 1; 300: one a side by default on the action clock
    ...(opts.heliSuppressor ? { heliSuppressor: true } : {}),   // 69.59 step 3 (286 (4)): off by default
    ...(opts.helipad ? { helipad: true } : {}),   // 69.74 step 2: off by default
    ...(opts.gunshipGun && opts.gunshipGun !== 40 ? { gunshipGun: opts.gunshipGun } : {}),   // 63.13: the 40 mm by default
    ...(opts.gunshipChin && opts.gunshipChin !== 30 ? { gunshipChin: opts.gunshipChin } : {}),   // 277 (2): the 30 mm by default
    ...(opts.buildKits ? { buildKits: true } : {}),   // 69.4 step 6a
    ...(opts.groundRules ? { groundRules: true } : {}),   // 69.4 step 6c
    ...(opts.footstepsHeard ? { footstepsHeard: true } : {}),
    ...(opts.relays ? { relays: true } : {}),
    ...(opts.pointDefence !== undefined ? { pointDefence: opts.pointDefence } : {}),   // 69.9; 300: on by default on the action clock
    ...(opts.airDefenceSites ? { airDefenceSites: true } : {}),   // 69.50 step 2a
    ...(opts.airDefenceSites && opts.siteRounds ? { siteRounds: true } : {}),   // 69.50 step 2b
    ...(opts.countermeasures !== undefined ? { countermeasures: opts.countermeasures } : {}),   // 69.58; 300: on by default on the action clock
    ...(opts.shipTurrets !== undefined ? { shipTurrets: opts.shipTurrets } : {}),   // 69.58 (271 (7)); 300: on by default on the action clock
    ...(opts.towedGuns !== undefined ? { towedGuns: opts.towedGuns } : {}),   // 69.13 (a); 300: on by default on the action clock, with the point defence (core/defaultOn.ts towedGunsOn)
    ...(opts.lighters ? { lighters: true } : {}),   // 69.10
    ...(opts.roughStart ? { roughStart: true } : {}),   // 69.2a
    ...(opts.storyObjectives ? { storyObjectives: true } : {}),   // 69.50 step 1
    ...(opts.storyCarry ? { storyCarry: true } : {}),   // 69.61 step 4
    ...(opts.storyEscort ? { storyEscort: true } : {}),   // 69.61 step 5
    ...(opts.intelRecord ? { intelRecord: true } : {}),   // 69.73 step 2a
    ...(opts.charges ? { charges: true } : {}),   // 69.47 step 1
    ...(opts.intelRecord && opts.baseIntel && opts.baseIntel !== 'none' ? { baseIntel: opts.baseIntel } : {}),   // 69.73 step 3
    ...(opts.hqHealth !== undefined ? { hqHealth: opts.hqHealth } : {}),   // 69.61 step 6 (DECISIONS 284 (a))
    ...(opts.technicalRole === false ? { technicalRole: false } : {}),   // 63.1r: on by default
    ...(opts.smallCraters ? { smallCraters: true } : {}),   // 63.11: off by default (the owner, Oct 13)
    ...(opts.burstPush ? { burstPush: true } : {}),   // §5.21 step 6: off by default
    ...(opts.blastModel !== undefined ? { blastModel: opts.blastModel } : {}),   // 69.65: on by default on the action clock, so off is passed too
    ...(opts.waveShips ? { waveShips: true } : {}),   // DECISIONS 200 step 4: off by default
    ...(opts.waveWalkPeople !== undefined && opts.waveWalkPeople >= 0 ? { waveWalkPeople: opts.waveWalkPeople } : {}),   // 207's follow-up
    ...(on(opts.suppression) && opts.burstSuppress ? { burstSuppress: true } : {}),   // §5.21 step 7: off by default
    ...(opts.frontPull === false ? { frontPull: false } : {}),   // 69.5f: on by default
    ...(opts.attention === false ? { attention: false } : {}),   // 181: on by default
    ...(opts.commanderPicture === false ? { commanderPicture: false } : {}),   // 182 (3): on by default
    ...(opts.efforts && (!Array.isArray(opts.efforts) || opts.efforts.some(Boolean)) ? { efforts: opts.efforts } : {}),   // §0.11 step D (or per team)
    ...(opts.contactBelief ? { contactBelief: true } : {}),   // 182 (2): off by default
    ...(opts.quietTier === true || (Array.isArray(opts.quietTier) && opts.quietTier.some(Boolean)) ? { quietTier: opts.quietTier } : {}),   // 62.60 lever 2: off by default

    ...(opts.marchTier === true || (Array.isArray(opts.marchTier) && opts.marchTier.some(Boolean)) ? { marchTier: opts.marchTier } : {}),   // 62.60 lever 2b: off by default
    ...(opts.steadyTier === true || (Array.isArray(opts.steadyTier) && opts.steadyTier.some(Boolean)) ? { steadyTier: opts.steadyTier } : {}),   // 62.60 lever 2c: off by default
    ...(opts.brainRate === false || (Array.isArray(opts.brainRate) && !opts.brainRate.every(Boolean)) ? { brainRate: opts.brainRate } : {}),   // 62.68: on by default, only off is passed
    ...((typeof opts.lookPeriod === 'number' && opts.lookPeriod > SIGHT_PERIOD_TICKS) || (Array.isArray(opts.lookPeriod) && opts.lookPeriod.some(v => v > SIGHT_PERIOD_TICKS)) ? { lookPeriod: opts.lookPeriod } : {}),   // 62.60, the look period in contact: today's 24 by default
    ...(opts.pictureDecay ? { pictureDecay: true } : {}),   // 182 (1): off by default
    ...((opts.vehicleSmoke ?? 0) > 0 ? { vehicleSmoke: opts.vehicleSmoke } : {}),   // 63.1u: none by default
    ...(opts.botVehicleSmoke ? { botVehicleSmoke: true } : {}),   // 63.1u: the bots' use, off by default
    ...(opts.crewmanRole !== undefined ? { crewmanRole: opts.crewmanRole } : {}),   // 69.46; 300: on by default on the action clock
    ...(opts.holdAndPush === false || (Array.isArray(opts.holdAndPush) && !opts.holdAndPush.every(Boolean)) ? { holdAndPush: opts.holdAndPush } : {}),   // 66.13: on by default (one value, or per team)
    ...(opts.flankTakesPoint === true || (Array.isArray(opts.flankTakesPoint) && opts.flankTakesPoint.some(Boolean)) ? { flankTakesPoint: opts.flankTakesPoint } : {}),   // 66.16: off by default (one value, or per team)
    ...(opts.standoffManoeuvre === true || (Array.isArray(opts.standoffManoeuvre) && opts.standoffManoeuvre.some(Boolean)) ? { standoffManoeuvre: opts.standoffManoeuvre } : {}),   // 66.15: off by default (one value, or per team)
    ...(opts.scoutRide === false ? { scoutRide: false } : {}),   // 63.1t: on by default
    ...(opts.mortarStore !== undefined ? { mortarStore: Math.max(MORTAR.storeRange[0], Math.min(MORTAR.storeRange[1], Math.round(opts.mortarStore))) } : {}),   // 69.23: the mortar's store
    ...(opts.mountStore !== undefined ? { mountStore: Math.max(MOUNT_STORE_RANGE[0], Math.min(MOUNT_STORE_RANGE[1], Math.round(opts.mountStore))) } : {}),   // 69.21: the launchers' store (2–6)
    ...(opts.tankStowage ? { tankStowage: { he: Math.max(TANK_GUN.stowageRange[0], Math.min(TANK_GUN.stowageRange[1], Math.round(opts.tankStowage.he))), solid: Math.max(TANK_GUN.stowageRange[0], Math.min(TANK_GUN.stowageRange[1], Math.round(opts.tankStowage.solid))) } } : {}),   // 69.29: the tanks' stowage dial
    ...(opts.denseWeb ? { denseWeb: true } : {}),
    ...(opts.drops ? { drops: true } : {}),
    ...(opts.fobs ? { fobs: true } : {}),
    ...(opts.munitionsSupply ? { munitionsSupply: true } : {}),   // 69.30 step 1
    ...(opts.letOutSlots !== undefined ? { letOutSlots: opts.letOutSlots } : {}),   // 69.38 step 2a: on unless false (ai/apcLoop.ts letOutSlot), so off is passed too
    ...(opts.convoys ? { convoys: true } : {}),   // 69.4 step 5
    ...(opts.droneDrops ? { droneDrops: true } : {}),   // 69.32: off by default
    ...(opts.buildCosts ? { buildCosts: true } : {}),   // 69.4 step 6b
    ...(rocketsOn(rocketsIn) ? { rockets: true } : {}),
    ...(rocketTeamsOf(rocketsIn) ? { rocketTeams: rocketTeamsOf(rocketsIn) } : {}),   // (S7 per side: absent when every side carries it)
    ...(layouts ? { layoutTeams: layouts.map(Boolean) } : {}),   // (65.32: the sides whose tubes are their members' own)
    ...(opts.measuredBodies ? { measuredBodies: true } : {}),
    ...(opts.suppression !== undefined ? { suppression: opts.suppression } : {}),   // 300: on by default on the action clock
    ...(on(opts.suppression) && opts.suppressionGrip && opts.suppressionGrip !== 'mark' ? { suppressionGrip: opts.suppressionGrip } : {}),
    ...(opts.entrench ? { entrench: true } : {}),
    ...(opts.foliageBullets ? { foliageBullets: true } : {}),
    ...(opts.foliageSight ? { foliageSight: true } : {}),
    ...(opts.teamPicture ? { teamPicture: true } : {}),
    ...(opts.personTuning ? { personTuning: opts.personTuning } : {}),
    ...(opts.clock === 'action' ? { actionTimeLimit: opts.actionTimeLimit ?? 180 } : {}),
  };

  const botOverrides = difficulty.map((d, i) => ({ difficulty: d, personality: personality[i] }));
  const map = generateMap(
    numPlanets, total, total, opts.mapSeed ?? opts.seed,
    undefined, undefined, opts.stars ?? 0, opts.wormholePairs ?? 0,
    difficulty[0], personality[0], botOverrides, undefined,
    1, units, teams, 0, teams, unitSlots,
    opts.hazardDensity ?? 'none', ruinDensity, undefined,
    opts.planetSize ?? 'mixed', opts.mapLayout ?? 'random', undefined, cbp, cbp, 'custom', opts.planetArchetype ?? autoArchetype(opts, numPlanets),
  );

  // Team loadouts the way the quick-match menu buys them, from each team's own credits
  const cbpFor = (t: number): number => opts.cbpByTeam?.[t] ?? cbp;
  const loadouts = new Map<number, { loadout: PlayerLoadout; fuel: number }>();
  for (let t = 0; t < teams; t++) {
    const pers = personalityFor(personality[t], teamBotParams(botParams, t));
    const rng = new SeededRandom(`${opts.seed}-botshop-${t}`);
    const r = computeBotLoadout(cbpFor(t) * unitsByTeam[t], pers, rng, mode, opts.weaponsByTeam?.[t]);
    loadouts.set(t, {
      loadout: {
        ammo: {
          standard: null,
          heavy: r.purchases.heavy ?? 0, cluster: r.purchases.cluster ?? 0, nuke: r.purchases.nuke ?? 0,
          probe: r.purchases.probe ?? 0, punch: r.purchases.punch ?? 0,
          mortar: r.purchases.mortar ?? 0,   // always listed: an absent count reads as unlimited (checkFire / hasAmmo)
        },
        upgrades: {},
      },
      fuel: r.fuelPurchased,
    });
  }

  // Kit precedence: this unit's → its team's → everyone's → the empty kit
  const kitSpec = (team: number, unit: number): KitPresetKey | UnitKit | undefined => opts.kitByUnit?.[team]?.[unit] ?? opts.kitByTeam?.[team] ?? opts.kit;
  const kitFor = (team: number, unit: number): UnitKit => { const k = kitSpec(team, unit); return k === undefined ? defaultUnitKit() : typeof k === 'string' ? kitPreset(k) : { ...k, launcher: { ...k.launcher }, suit: { ...k.suit }, equipment: [...k.equipment], traits: { ...k.traits } }; };
  const unitSeen = new Map<number, number>();
  let players: PlayerState[] = map.players.map(p => {
    const lo = loadouts.get(p.teamIndex);
    const unit = unitSeen.get(p.teamIndex) ?? 0;
    unitSeen.set(p.teamIndex, unit + 1);
    // Unit kit → modifiers, the way Game.startGame resolves them (movement and jump bonus follow the record)
    const kit = kitFor(p.teamIndex, unit);
    const modifiers = resolveUnitModifiers(kit, { previewQuality: 1 });
    const values = teamBotParams(botParams, p.teamIndex);
    const skill = skillFor(difficulty[p.teamIndex], values);
    const pers = personalityFor(personality[p.teamIndex], values);
    const config: BotConfig = {
      ...(p.botConfig ?? { skill, personality: pers }), skill, personality: pers,
      ...(opts.skillProfiles === false ? { skillProfile: false } : {}),
      ...(opts.botTuning || opts.botTuningByTeam?.[p.teamIndex]
        ? { tuning: { ...(p.botConfig?.tuning ?? {}), ...(opts.botTuning ?? {}), ...(opts.botTuningByTeam?.[p.teamIndex] ?? {}) } }
        : {}),
    };
    // 62.48: the catalogue's turn knobs, layered as the bot resolves its tuning (botParamsTurn withTurnParams)
    const tuning = withTurnParams(values, config);
    return {
      ...p,
      isBot: true,
      botConfig: tuning === config.tuning ? config : { ...config, tuning },
      loadout: lo ? lo.loadout : p.loadout,
      jumppackFuel: lo ? lo.fuel : p.jumppackFuel,
      cbp: cbpFor(p.teamIndex),
      kit, modifiers,
      movementRange: modifiers.movementRange,
      movementRemaining: modifiers.movementRange,
      jumpBonus: modifiers.jumpVelocityBonus,
    };
  });

  // Stakes per unit the way Game.startGame sets them
  const stakesPerUnit = modeConfig.stakesPerUnit;
  if (stakesPerUnit) players = players.map(p => ({ ...p, flagsRemaining: stakesPerUnit }));

  let state: GameState = {
    ...createInitialGameState(config),
    ...(archetypes ? { botArchetypes: archetypes } : {}),   // hidden from the player; the harness reports them
    planets: opts.planetsOverride && opts.planetsOverride.length === map.planets.length ? opts.planetsOverride : map.planets,
    players, structures: [], hazards: map.hazards, ruins: map.ruins,
    flora: map.flora, rocks: map.rocks, environmentalEffects: map.environmentalEffects, carryables: map.carryables,
    collectibles: map.collectibles,
  };

  // R13: what earlier battles here destroyed stays destroyed
  if (opts.destroyedProps) {
    const dead = { rocks: new Set(opts.destroyedProps.rocks), flora: new Set(opts.destroyedProps.flora), hazards: new Set(opts.destroyedProps.hazards) };
    state = {
      ...state,
      rocks: state.rocks.map(r => (dead.rocks.has(r.id) ? { ...r, alive: false } : r)),
      flora: state.flora.map(f => (dead.flora.has(f.id) ? { ...f, alive: false } : f)),
      hazards: state.hazards.map(h => (dead.hazards.has(h.id) ? { ...h, alive: false } : h)),
    };
  }
  // R11 step 2 (lane D): squads in the state
  if (opts.squadsByTeam) state = { ...state, squads: squadsFromGroups(state.players, opts.squadsByTeam) };
  else if (layouts || opts.squadSize || opts.formation || opts.extraction || opts.callIns || opts.marks?.length || opts.beacons || opts.relays || opts.fluidSquads || opts.squadLead) state = { ...state, squads: Array.from({ length: teams }, (_, t) => (layouts?.[t] ? squadsFromGroups(state.players, Array.from({ length: t + 1 }, (_, i) => (i === t ? layoutGroups(layouts[t]!, opts.squadTypes) : undefined))).map((sq, i) => ({ ...sq, id: t * 1000 + i })) : squadsForTeam(state.players, t, opts.squadSize ?? 3, t * 1000))).flat() };
  if (opts.formation && state.squads) {
    const formation = { shape: opts.formation.shape, spacing: opts.formation.spacing ?? DEFAULT_FORMATION_SPACING };
    state = { ...state, squads: state.squads.map(sq => ({ ...sq, formation })) };
  }
  // 68.9 / decision 139: with squad leaders on, each squad's first unit is its designated leader (no promotion)
  // decision 148: leaders lead wherever there are squads on the action clock (the config says so only there: a duel's state is
  // as it was, to the checksum)
  if (leadOn(opts) && state.squads?.length) state = { ...state, config: { ...state.config, squadLead: opts.squadLead ?? true }, squads: state.squads.map(sq => (sq.unitIds.length ? { ...sq, leader: sq.unitIds[0] } : sq)) };
  // Mode layers: objective placements, environment schedules, respawn pools
  state = setupModeLayers(state);
  // As the game: flora destruction tracking, match events, turn order
  state = completeMatchState(state);

  // Deploy: bots pick planets (home / depot planets when the objective assigns them), units fan out
  let ds = initDeployState(players, teams, 0);
  ds = autoSelectBotPlanetsForMode(
    ds, state.planets, players, teams, 0, new SeededRandom(`${opts.seed}-deploy`),
    state.config.modeConfig, state.modeState, state.config.modeConfig.homeTeamPlanets, opts.deployRegionByTeam,
  );
  players = applyDeployToPlayers(players, ds.selections, new SeededRandom(`${opts.seed}-deploy-apply`));
  const fan = computeFanOutTargets(players, ds.selections, state.planets);
  players = players.map(p => {
    const t = fan.find(x => x.playerId === p.id);
    return t ? { ...p, surfaceDir: t.endDir } : p;
  });
  state = { ...state, players, deployState: { ...ds, done: true } };
  // Phase 59.5: in real time nobody crosses space — an action battle is fought on one world
  // 69.6a: on the Foothold board the two bases stand a set share of the circumference apart instead
  if (state.config.clock === 'action') {
    const separation = boardSeparation(opts.baseSeparation);
    state = separation !== undefined ? layOutBoard(gatherOnBattlePlanet(state), separation) : layOutFront(gatherOnBattlePlanet(state), opts.frontHeights);
    players = state.players;
  }

  // Home planets, cores and depots follow the deployed positions
  state = finalizeModeDeploy(state);

  // Fog from the deployed positions. Decision 104: on the action clock one fog per team, revealed by what each
  // person sees (core/teamFog.ts); the turn clock keeps a fog per unit, revealed to the horizon.
  const colors = state.planets.map(p => new Float32Array(p.heightmap.length * 3));
  let fog: FogState[];
  if (state.config.clock === 'action') {
    fog = teamFogs(players, state.planets);
    if (liveTeamFog(state)) stepTeamFog({ ...state, fog }, pi => colors[pi], 0, true);
    else {
      // classic scale: each team's fog is the union of its units' horizon reveals, once (the turn clock's rule)
      const byTeam = new Map<FogState, FogState>();
      players.forEach((p, i) => byTeam.set(fog[i], updateFogForPlayer(byTeam.get(fog[i]) ?? fog[i], p, state.planets, colors, state.turnNumber, false)));
      fog = fog.map(f => byTeam.get(f)!);
    }
  } else {
    fog = initializeFog(players.length, state.planets);
    fog = fog.map((f, i) => updateFogForPlayer(f, players[i], state.planets, colors, state.turnNumber, false));
  }
  state = startAimingPhase({ ...state, fog });

  // Pre-placed structures (sector: what the holder built; a team's first unit owns them, or nobody)
  if (opts.structuresOverride?.length) {
    const structures: StructureState[] = opts.structuresOverride.map((b, id) => {
      const owner = b.ownerTeam === null ? undefined : state.players.filter(p => p.teamIndex === b.ownerTeam).sort((x, y) => x.id - y.id)[0];
      const seats = initialSeat(b.type);
      return { id, type: b.type, planetIndex: b.planetIndex, surfaceDir: vecNormalize(b.surfaceDir), health: structureHealth(b.type), ownerId: owner?.id ?? -1, ...(seats ? { seats } : {}) };
    });
    state = { ...state, structures: [...structures, ...state.structures.map(s => ({ ...s, id: s.id + structures.length }))] };
  }

  // 63.1l (the owner: "currently apcs spawn in facing away from the objective, would save a lot of trouble to have them spawn
  // in facing the objective"): a vehicle parked for team `t` at `dir` faces its objective — the nearest capture point its
  // side does not hold, else the nearest enemy — the tangent there toward it; null with neither (the placement's own)
  const faceObjective = (st: typeof state, t: number, dir: Vec3): Vec3 | null => {
    const n = vecNormalize(dir);
    const planetIndex = st.players.find(p => p.teamIndex === t)?.planetIndex ?? 0;
    const R = st.planets[planetIndex]?.radius ?? 1;
    const sites = (st.modeState.points?.sites ?? []).filter(s => s.planetIndex === planetIndex && s.owner !== t);
    const foes = st.players.filter(p => p.teamIndex !== t && p.planetIndex === planetIndex);
    const targets = sites.length ? sites.map(s => s.surfaceDir) : foes.map(p => p.surfaceDir);
    if (!targets.length) return null;
    const goal = targets.reduce((a, b) => arcDistance(b, n, R) < arcDistance(a, n, R) ? b : a);
    const to = projectToTangent(vecSub(vecNormalize(goal), n), n);
    return vecLength(to) > 1e-9 ? vecNormalize(to) : null;
  };

  // X31: motorcycles parked on a ring two and a half people from each team's first unit, facing out (gas, electric, gas…)
  if (sides.some(f => f.bikes > 0) && state.config.clock === 'action') {
    const teams = [...new Set(state.players.map(p => p.teamIndex))].sort((a, b) => a - b);
    const extra: StructureState[] = [];
    for (const t of teams) {
      const lead = state.players.filter(p => p.teamIndex === t).sort((x, y) => x.id - y.id)[0];
      const planet = lead ? state.planets[lead.planetIndex] : undefined;
      if (!lead || !planet) continue;
      const n = vecNormalize(lead.surfaceDir);
      const ref = Math.abs(n.y) < 0.9 ? { x: 0, y: 1, z: 0 } : { x: 1, y: 0, z: 0 };
      const u = vecNormalize(vecCross(n, ref)), v = vecCross(n, u);
      const bikes = sideOf(t).bikes;   // (69.49: the side's own)
      for (let k = 0; k < bikes; k++) {
        const ang = (k / bikes) * Math.PI * 2 + 1.3;
        const out = vecAdd(vecScale(u, dcos(ang)), vecScale(v, dsin(ang)));
        const arc = (2.5 * PERSON_BODY_HEIGHT * personSetOf(state).k) / planet.radius;
        const dir = vecNormalize(vecAdd(vecScale(n, dcos(arc)), vecScale(out, dsin(arc))));
        const variant = opts.bikeVariant ?? (k % 2 === 0 ? 'gas' : 'electric');
        extra.push({ id: state.structures.length + extra.length, type: 'motorcycle', planetIndex: lead.planetIndex, surfaceDir: dir, health: structureHealth('motorcycle'), ownerId: lead.id,
          seats: initialSeat('motorcycle'), drive: { heading: faceObjective(state, t, dir) ?? vecNormalize(out), speed: 0, variant } });
      }
    }
    state = { ...state, structures: [...state.structures, ...extra] };
  }

  // S6 harness: empty emplacements per team. At a person scale on the action clock they stand behind the lines
  // (decision 126, the owner: "guns behind the lines"); otherwise a short walk from the team's first unit, one per step
  // around it (64.11).
  if (opts.emplacementsPerTeam && opts.emplacementsPerTeam > 0) {
    const teams = [...new Set(state.players.map(p => p.teamIndex))].sort((a, b) => a - b);
    const extra: StructureState[] = [];
    const behindLines = (opts.gunsAt ?? 'lines') === 'lines' && state.config.clock === 'action' && personSetOf(state).k < 1;
    const dumps: Array<{ planetIndex: number; surfaceDir: Vec3; ownerId: number; teamIndex: number }> = [];
    for (const t of teams) {
      const lead = state.players.filter(p => p.teamIndex === t).sort((x, y) => x.id - y.id)[0];
      const planet = lead ? state.planets[lead.planetIndex] : undefined;
      if (!lead || !planet) continue;
      const line = behindLines
        ? gunsBehindTheLines(state.players, t, lead.planetIndex, planet.radius, opts.emplacementsPerTeam,
          structureBlastReach(WEAPONS.standard.blastRadius, planet.radius, personScaleStructureHalfWidth({ type: 'emplacement' }, state.config)),
          3 * PERSON_BODY_HEIGHT * personSetOf(state).k)
        : null;
      // decision 128: behind the lines, the guns' shells wait in a dump beside them — out of the front's blasts (one
      // heavy shell's reach covers a person-scale skirmish's whole front, its crates too)
      if (line?.dump && gunStockOn(state)) dumps.push({ planetIndex: lead.planetIndex, surfaceDir: line.dump, ownerId: lead.id, teamIndex: t });
      const dirs = line ? line.guns : Array.from({ length: opts.emplacementsPerTeam }, (_, k) => {
          const ang = (k / opts.emplacementsPerTeam!) * Math.PI * 2 + 0.5;
          const n = vecNormalize(lead.surfaceDir);
          const ref = Math.abs(n.y) < 0.9 ? { x: 0, y: 1, z: 0 } : { x: 1, y: 0, z: 0 };
          const u = vecNormalize(vecCross(n, ref)), v = vecCross(n, u);
          // 64.11: on the action clock, within a person's reach of the first unit (2.5 u is 45 people at 1/20); the turn game's 2.5 u otherwise
          const arc = (state.config.clock === 'action' ? 0.8 * personSetOf(state).seatReach : 2.5) / planet.radius;
          return vecNormalize(vecAdd(vecScale(n, dcos(arc)), vecScale(vecAdd(vecScale(u, dcos(ang)), vecScale(v, dsin(ang))), dsin(arc))));
        });
      for (const dir of dirs) {
        extra.push({ id: state.structures.length + extra.length, type: 'emplacement', planetIndex: lead.planetIndex, surfaceDir: dir, health: structureHealth('emplacement'), ownerId: lead.id, seats: initialSeat('emplacement') });
      }
    }
    for (const d of dumps) {
      extra.push({ id: state.structures.length + extra.length, type: 'supply', planetIndex: d.planetIndex, surfaceDir: d.surfaceDir, health: structureHealth('supply'), ownerId: d.ownerId, supply: { teamIndex: d.teamIndex, bandages: 0, magazines: 0, shells: GUN_STOCK.crate } });
    }
    state = { ...state, structures: [...state.structures, ...extra] };
  }

  // 63.1d: transports parked behind each team — on the side away from the enemy, clear of its spawn ring and the walk out
  // of it (the coordinator, from lane B's every-switch run: parked six people out among the spawns, bots walking out
  // hopped against it) by its measured footprint, a person's width and a two-person walk — facing away, the ramp toward
  // the team (core/actionSetup.ts lineBehindTheTeam, the guns' line behind the team)
  // (63.1o: the technicals after them on the same line — with none, every 6×6's spot as it was)
  // (69.4 step 5: with convoys, the supply trucks after them — CONVOY.trucks a side, DECISIONS 166)
  // (197 step 1: the tracked carriers — the M113 — after the 6×6s; with none, every spot as it was)
  // (69.49: each side's own counts — the same for every side unless its force is composed)
  const parkedOf = (t: number): Array<'wheeled' | 'tracked' | 'tank' | 'technical' | 'supplyTruck'> => { const f = sideOf(t); return [...Array<'wheeled'>(f.transports).fill('wheeled'), ...Array<'tracked'>(f.tracked).fill('tracked'), ...Array<'tank'>(f.tanks).fill('tank'), ...Array<'technical'>(f.technicals).fill('technical'), ...Array<'supplyTruck'>(convoysOn(state.config) ? CONVOY.trucks : 0).fill('supplyTruck')]; };
  if (Array.from({ length: teams }, (_, t) => parkedOf(t)).some(l => l.length > 0) && state.config.clock === 'action' && personSetOf(state).k < 1) {
    const teams = [...new Set(state.players.map(p => p.teamIndex))].sort((a, b) => a - b);
    const extra: StructureState[] = [];
    const ps = personSetOf(state), person = PERSON_BODY_HEIGHT * ps.k;
    for (const t of teams) {
      const lead = state.players.filter(p => p.teamIndex === t).sort((x, y) => x.id - y.id)[0];
      const planet = lead ? state.planets[lead.planetIndex] : undefined;
      const parked = parkedOf(t);
      if (!lead || !planet || !parked.length) continue;
      const clear = Math.max(...[...new Set(parked)].map(type => structureFootprint({ type }, state.config, planet.radius))) + ps.unitRadius + 2 * person;
      const line = lineBehindTheTeam(state.players, t, lead.planetIndex, planet.radius, parked.length, 5 * person, clear);
      if (!line) continue;
      line.spots.forEach((spot, k) => {
        const type = parked[k], dial = DRIVE_DIALS[type];
        // on ground it can start up: no steeper round it than the grade it climbs at half its flat acceleration
        const startGrade = dial.gas.accel / (2 * dial.hill * (1 - dial.gas.climb));
        const dir = flatSpotNear(planet, spot, line.back, person, clear, startGrade);
        extra.push({ id: state.structures.length + extra.length, type, planetIndex: lead.planetIndex, surfaceDir: dir, health: structureHealth(type), ownerId: lead.id,
          seats: initialSeat(type, undefined, state.config.tankStowage), drive: { heading: faceObjective(state, t, dir) ?? line.back, speed: 0, variant: 'gas' } });
      });
    }
    // 69.21: the technicals armed by the setup's list, cycled per side (the HMG left as it is); 69.23: the tracked carriers by theirs
    const store = opts.mortarStore !== undefined ? Math.max(MORTAR.storeRange[0], Math.min(MORTAR.storeRange[1], Math.round(opts.mortarStore))) : MORTAR.store;
    // (69.49: each side's own lists)
    for (const type of ['technical', 'tracked'] as const) {
      const nth = new Map<number, number>();
      for (let i = 0; i < extra.length; i++) {
        const v = extra[i];
        if (v.type !== type) continue;
        const f = sideOf(state.players.find(p => p.id === v.ownerId)?.teamIndex ?? 0), arms = type === 'technical' ? f.technicalArmaments : f.trackedArmaments;
        if (!arms?.length || !arms.some(a => a !== 'hmg')) continue;
        const k = nth.get(v.ownerId) ?? 0;
        nth.set(v.ownerId, k + 1);
        const a = arms[k % arms.length];
        if (a !== 'hmg') extra[i] = armStructure(v, a, store);
      }
    }
    state = { ...state, structures: [...state.structures, ...extra] };
  }

  // 65.22: the emplaced HMGs — each team's covers its front: eight people ahead of its first unit toward the nearest enemy,
  // side by side five people apart (settled clear of the rest at the setup's end, core/buildingScale.ts settleBuildings)
  if (sides.some(f => f.hmgs > 0) && state.config.clock === 'action' && personSetOf(state).k < 1) {
    const person = PERSON_BODY_HEIGHT * personSetOf(state).k;
    const teams = [...new Set(state.players.map(p => p.teamIndex))].sort((a, b) => a - b);
    const extra: StructureState[] = [];
    for (const t of teams) {
      const lead = state.players.filter(p => p.teamIndex === t).sort((x, y) => x.id - y.id)[0];
      const planet = lead ? state.planets[lead.planetIndex] : undefined;
      if (!lead || !planet) continue;
      const n = vecNormalize(lead.surfaceDir);
      const foe = state.players.filter(p => p.teamIndex !== t && p.planetIndex === lead.planetIndex)
        .sort((a, b) => arcDistance(a.surfaceDir, n, planet.radius) - arcDistance(b.surfaceDir, n, planet.radius) || a.id - b.id)[0];
      const toward = foe ? projectToTangent(vecSub(vecNormalize(foe.surfaceDir), n), n) : surfaceTangentFrame(n).tx;
      const ahead = vecLength(toward) > 1e-9 ? vecNormalize(toward) : surfaceTangentFrame(n).tx, side = vecCross(n, ahead);
      const hmgs = sideOf(t).hmgs;   // (69.49: the side's own)
      for (let k = 0; k < hmgs; k++) {
        const off = (k - (hmgs - 1) / 2) * 5 * person;
        const dir = vecNormalize(vecAdd(vecScale(n, planet.radius), vecAdd(vecScale(ahead, 8 * person), vecScale(side, off))));
        // 65.22 (2): its ring's firing gap toward the enemy (the side's front, ahead, along the ground where it stands)
        const yaw = yawFacing(dir, projectToTangent(ahead, dir));
        extra.push({ id: state.structures.length + extra.length, type: 'hmg', planetIndex: lead.planetIndex, surfaceDir: dir, health: structureHealth('hmg'), ownerId: lead.id, seats: initialSeat('hmg'), ...(yaw ? { yaw } : {}) });
      }
    }
    state = { ...state, structures: [...state.structures, ...extra] };
  }

  // X18 step 2 (harness): each team's installations on a ring round its first unit, owned by it
  if (opts.installationsPerTeam?.length) {
    const teams = [...new Set(state.players.map(p => p.teamIndex))].sort((a, b) => a - b);
    const extra: StructureState[] = [];
    for (const t of teams) {
      const lead = state.players.filter(p => p.teamIndex === t).sort((x, y) => x.id - y.id)[0];
      // on the Foothold board (69.6a) round the side's base, not its first unit (a push's defenders start forward, 69.6c),
      // less the HQ and the landing pad the base lays itself (below)
      const base = state.board?.bases.find(b => b.team === t);
      const planetIndex = base ? base.planetIndex : lead?.planetIndex ?? -1;
      const planet = lead ? state.planets[planetIndex] : undefined;
      if (!lead || !planet) continue;
      const n = vecNormalize(base ? base.dir : lead.surfaceDir);
      const ref = Math.abs(n.y) < 0.9 ? { x: 0, y: 1, z: 0 } : { x: 1, y: 0, z: 0 };
      const u = vecNormalize(vecCross(n, ref)), v = vecCross(n, u);
      const arc = 2.5 / planet.radius;
      opts.installationsPerTeam.forEach((type, k) => {
        if (base && BOARD_BASE_LAYS.has(type)) return;
        const ang = (k / opts.installationsPerTeam!.length) * Math.PI * 2 + 2.0;   // offset from the emplacements' ring (+0.5)
        const dir = vecNormalize(vecAdd(vecScale(n, dcos(arc)), vecScale(vecAdd(vecScale(u, dcos(ang)), vecScale(v, dsin(ang))), dsin(arc))));
        const seats = initialSeat(type);
        extra.push({ id: state.structures.length + extra.length, type, planetIndex, surfaceDir: dir, health: structureHealth(type), ownerId: lead.id, ...(seats ? { seats } : {}) });
      });
    }
    state = { ...state, structures: [...state.structures, ...extra] };
  }

  // 69.6a: each base's buildings — the HQ (the team's building), its landing pad and its supply dump, abreast behind its
  // people on the side away from the other base, clear of them by the larger building's footprint and a two-person walk
  // (core/actionSetup.ts lineBehindTheTeam, the guns' line), owned by the team's first unit; settled with the rest at the end
  if (state.board) {
    const ps = personSetOf(state), person = PERSON_BODY_HEIGHT * ps.k;
    const extra: StructureState[] = [];
    const bases = state.board.bases.map(base => {
      const lead = state.players.filter(p => p.teamIndex === base.team).sort((x, y) => x.id - y.id)[0];
      const planet = state.planets[base.planetIndex];
      if (!lead || !planet) return base;
      const builder = builderOf(base.team);
      const half = Math.max(structureFootprint({ type: 'ecotourism', builder }, state.config, planet.radius), structureFootprint({ type: 'landingPad', builder }, state.config, planet.radius));
      // the base stays at its home end: where the side's people stand away from it (a push's defenders start at their landing,
      // 69.6c, laid before this) the line is laid behind a stand-in at the base's centre instead of behind them
      const near = dcos(BOARD_HOME_PEOPLE * person / planet.radius);
      const atHome = state.players.every(p => p.teamIndex !== base.team || (p.planetIndex === base.planetIndex && vecDot(vecNormalize(p.surfaceDir), vecNormalize(base.dir)) >= near));
      const people = atHome ? state.players : [...state.players.filter(p => p.teamIndex !== base.team), { ...lead, planetIndex: base.planetIndex, surfaceDir: base.dir }];
      const line = lineBehindTheTeam(people, base.team, base.planetIndex, planet.radius, 3, 2 * half + 2 * person, half + ps.unitRadius + 2 * person);
      if (!line) return base;
      const id = (k: number) => state.structures.length + extra.length + k;
      const [pad, hq, dump] = [id(0), id(1), id(2)], mountIds = POINT_DEFENCE.perBase.map((_, k) => id(3 + k));   // (69.9: the mounts' ids before anything is pushed — `id` counts what is)
      const gunShells = gunStockOn(state) && state.structures.some(g => bodyHasSeat(g.type) && state.players.find(p => p.id === g.ownerId)?.teamIndex === base.team);
      extra.push(
        { id: pad, type: 'landingPad', planetIndex: base.planetIndex, surfaceDir: line.spots[0], health: structureHealth('landingPad'), ownerId: lead.id },
        { id: hq, type: 'ecotourism', planetIndex: base.planetIndex, surfaceDir: line.spots[1], health: state.config.hqHealth ?? structureHealth('ecotourism'), ownerId: lead.id },   // (69.61 step 6: GameConfig.hqHealth)
        { id: dump, type: 'supply', planetIndex: base.planetIndex, surfaceDir: line.spots[2], health: structureHealth('supply'), ownerId: lead.id,
          supply: { teamIndex: base.team, bandages: SUPPLY.bandages, magazines: SUPPLY.magazines, ...(gunShells ? { shells: GUN_STOCK.crate } : {}) } },
      );
      // 69.9: the base's point defence — a mount of each variant to either side of the HQ, across the line (placed with the base, the owner's "for now").
      // 63.16 (j): across it — the flank, perpendicular to the pad–HQ–stores line: `along` is that line's direction, and the mounts used to be
      // laid along it toward the pad and the stores, on their claims, so the planner shoved every one (median 8 people); offPeople 19 on the
      // flank clears the HQ's deck, its skirt and the clearance on level ground, and the pad and the stores lie off at the line's length
      if (defaultOn(state.config, 'pointDefence')) {
        const along = vecNormalize(vecSub(line.spots[2], line.spots[0])), hqDir = vecNormalize(line.spots[1]);
        const lineDir = vecNormalize(vecSub(along, vecScale(hqDir, vecDot(along, hqDir))));
        const side = vecNormalize(vecCross(hqDir, lineDir));
        POINT_DEFENCE.perBase.forEach((variant, k) => {
          const t = vecScale(side, k % 2 === 0 ? 1 : -1), arc = (POINT_DEFENCE.offPeople * person) / planet.radius;
          const dir = vecNormalize(vecAdd(vecScale(hqDir, dcos(arc)), vecScale(t, dsin(arc))));
          extra.push({ id: mountIds[k], type: 'pointDefence', planetIndex: base.planetIndex, surfaceDir: dir, health: structureHealth('pointDefence'), ownerId: lead.id, pd: { variant, magazine: POINT_DEFENCE.variants[variant].magazine } });
        });
      }
      // 69.13 (a): the towed guns, deployed behind the base — `behindPeople` behind the HQ away from the enemy's base, abreast
      // across that line `apartPeople` apart, each facing the enemy's base; the planner settles them with the rest (BASE_PLAN.order)
      const enemy = state.board!.bases.find(b => b.team !== base.team && b.planetIndex === base.planetIndex);
      if (towedGunsOn(state.config) && enemy) {
        const hqDir = vecNormalize(line.spots[1]), toward = vecSub(vecNormalize(enemy.dir), vecScale(hqDir, vecDot(vecNormalize(enemy.dir), hqDir)));
        if (vecLength(toward) > 1e-9) {
          const fwd = vecNormalize(toward), across = vecNormalize(vecCross(hqDir, fwd)), back = (TOWED_GUNS.behindPeople * person) / planet.radius;
          const behind = vecNormalize(vecAdd(vecScale(hqDir, dcos(back)), vecScale(fwd, -dsin(back))));
          for (let k = 0; k < TOWED_GUNS.perBase; k++) {
            const off = ((k - (TOWED_GUNS.perBase - 1) / 2) * TOWED_GUNS.apartPeople * person) / planet.radius;
            const dir = vecNormalize(vecAdd(vecScale(behind, dcos(off)), vecScale(across, dsin(off))));
            const face = vecSub(fwd, vecScale(dir, vecDot(fwd, dir)));
            extra.push({ id: id(0), type: 'towedGun', planetIndex: base.planetIndex, surfaceDir: dir, health: structureHealth('towedGun'), ownerId: lead.id, yaw: yawFacing(dir, vecNormalize(face)) });
          }
        }
      }
      // 69.74 step 2 (DECISIONS 302): the side's helipad — HELI_PAD.behindPeople behind the HQ away from the enemy's base and
      // asidePeople across (clear of the towed guns' line), facing the enemy's base (its spots a row across); the planner settles it
      if (helipadOn(state.config) && enemy) {
        const hqDir = vecNormalize(line.spots[1]), toward = vecSub(vecNormalize(enemy.dir), vecScale(hqDir, vecDot(vecNormalize(enemy.dir), hqDir)));
        if (vecLength(toward) > 1e-9) {
          const fwd = vecNormalize(toward), across = vecNormalize(vecCross(hqDir, fwd));
          const back = (HELI_PAD.behindPeople * person) / planet.radius, aside = (HELI_PAD.asidePeople * person) / planet.radius;
          const behind = vecNormalize(vecAdd(vecScale(hqDir, dcos(back)), vecScale(fwd, -dsin(back))));
          const dir = vecNormalize(vecAdd(vecScale(behind, dcos(aside)), vecScale(across, dsin(aside))));
          const face = vecSub(fwd, vecScale(dir, vecDot(fwd, dir)));
          extra.push({ id: id(0), type: 'helipad', planetIndex: base.planetIndex, surfaceDir: dir, health: HELI_PAD.health, ownerId: lead.id, yaw: yawFacing(dir, vecNormalize(face)) });
        }
      }
      // 69.50 step 2a (DECISIONS 261, 263): the base's forward air-defence site — derived from the setup, `forwardShare` of the way
      // from the HQ toward the enemy's base on the great circle between them (the attacker's way in), slid back toward home while a
      // capture point's circle is within `pointClearPeople` of it (a site is not an objective's furniture), facing the enemy's base;
      // the planner settles it with the rest (BASE_PLAN.order: the point defence). 69.50 step 2b (DECISIONS 265 (3c)): an
      // operation's named sites stand instead (`airDefenceSitesAt`), and each site has its generator by it (below)
      const named = state.config.airDefenceSites ? opts.airDefenceSitesAt?.filter(n => n.team === base.team) : undefined;
      const siteSpots: Array<{ planetIndex: number; dir: Vec3 }> = (named ?? []).map(n => ({ planetIndex: n.planetIndex, dir: vecNormalize(n.surfaceDir) }));
      if (state.config.airDefenceSites && !named && enemy) {
        const from = vecNormalize(line.spots[1]), to = vecNormalize(enemy.dir), whole = dacos(Math.max(-1, Math.min(1, vecDot(from, to))));
        const axis = vecCross(from, to);
        if (whole > 1e-6 && vecLength(axis) > 1e-9) {
          const side = vecCross(vecNormalize(axis), from), A = AIR_DEFENCE_SITE;
          const at = (arc: number) => vecNormalize(vecAdd(vecScale(from, dcos(arc)), vecScale(side, dsin(arc))));
          const points = (state.modeState.points?.sites ?? []).filter(p => p.planetIndex === base.planetIndex);
          const near = (d: Vec3) => points.some(p => dacos(Math.max(-1, Math.min(1, vecDot(vecNormalize(p.surfaceDir), d)))) * planet.radius < ps.captureRadius + A.pointClearPeople * person);
          const slide = (A.slidePeople * person) / planet.radius;
          let arc = whole * A.forwardShare;
          while (arc > slide && near(at(arc))) arc -= slide;
          siteSpots.push({ planetIndex: base.planetIndex, dir: at(arc) });
        }
      }
      // each site facing the enemy's base (on its planet), and its generator `behindPeople` behind it toward the HQ and
      // `asidePeople` across, facing it — the site's `pd.power`
      for (const { planetIndex, dir } of siteSpots) {
        const R = state.planets[planetIndex]?.radius;
        if (!R) continue;
        const toE = enemy && enemy.planetIndex === planetIndex ? vecSub(vecNormalize(enemy.dir), vecScale(dir, vecDot(vecNormalize(enemy.dir), dir))) : null;
        const homeDir = vecNormalize(line.spots[1]), toHome = planetIndex === base.planetIndex ? vecSub(homeDir, vecScale(dir, vecDot(homeDir, dir))) : null;
        const back = toHome && vecLength(toHome) > 1e-9 ? vecNormalize(toHome) : toE && vecLength(toE) > 1e-9 ? vecScale(vecNormalize(toE), -1) : surfaceTangentFrame(dir).tx;
        const across = vecNormalize(vecCross(dir, back)), G = AIR_DEFENCE_SITE.generator;
        const b = (G.behindPeople * person) / R, a = (G.asidePeople * person) / R;
        const behind = vecNormalize(vecAdd(vecScale(dir, dcos(b)), vecScale(back, dsin(b))));
        const genDir = vecNormalize(vecAdd(vecScale(behind, dcos(a)), vecScale(across, dsin(a))));
        const toSite = vecSub(dir, vecScale(genDir, vecDot(dir, genDir)));
        const siteId = id(0), genId = siteId + 1;
        extra.push({ id: siteId, type: 'pointDefence', planetIndex, surfaceDir: dir, health: structureHealth('pointDefence'), ownerId: lead.id, pd: { variant: 'site', power: genId, magazine: POINT_DEFENCE.variants.site.magazine }, ...(toE && vecLength(toE) > 1e-9 ? { yaw: yawFacing(dir, vecNormalize(toE)) } : {}) });
        extra.push({ id: genId, type: 'generator', planetIndex, surfaceDir: genDir, health: structureHealth('generator'), ownerId: lead.id, ...(vecLength(toSite) > 1e-9 ? { yaw: yawFacing(genDir, vecNormalize(toSite)) } : {}) });
      }
      return { ...base, hq, pad, dump };
    });
    state = { ...state, structures: [...state.structures, ...extra], board: { ...state.board, bases } };
  }

  // 65.9: each team's supply crate, three people out from its first unit (offset from the emplacements' and towers' rings)
  const crates = opts.suppliesPerTeam ?? (state.config.clock === 'action' && state.config.medical !== false ? SUPPLY.crates : 0);
  if (crates > 0) {
    const teams = [...new Set(state.players.map(p => p.teamIndex))].sort((a, b) => a - b);
    const out = 3 * PERSON_BODY_HEIGHT * personSetOf(state).k;
    const extra: StructureState[] = [];
    for (const t of teams) {
      const lead = state.players.filter(p => p.teamIndex === t).sort((x, y) => x.id - y.id)[0];
      const planet = lead ? state.planets[lead.planetIndex] : undefined;
      if (!lead || !planet) continue;
      // decision 128: with a gun stock and its guns at hand, a team's crates carry shells for them
      const gunShells = gunStockOn(state) && state.structures.some(g => bodyHasSeat(g.type) && state.players.find(p => p.id === g.ownerId)?.teamIndex === t)
        && !state.structures.some(c => structureJob(c).supply && c.supply?.teamIndex === t && (c.supply.shells ?? 0) > 0);   // no dump behind the lines
      for (let k = 0; k < crates; k++) {
        const ang = (k / crates) * Math.PI * 2 + 3.7;
        const n = vecNormalize(lead.surfaceDir);
        const ref = Math.abs(n.y) < 0.9 ? { x: 0, y: 1, z: 0 } : { x: 1, y: 0, z: 0 };
        const u = vecNormalize(vecCross(n, ref)), v = vecCross(n, u);
        const arc = out / planet.radius;
        const dir = vecNormalize(vecAdd(vecScale(n, dcos(arc)), vecScale(vecAdd(vecScale(u, dcos(ang)), vecScale(v, dsin(ang))), dsin(arc))));
        extra.push({ id: state.structures.length + extra.length, type: 'supply', planetIndex: lead.planetIndex, surfaceDir: dir, health: structureHealth('supply'), ownerId: lead.id, supply: { teamIndex: t, bandages: SUPPLY.bandages, magazines: SUPPLY.magazines , ...(gunShells ? { shells: GUN_STOCK.crate } : {}) } });
      }
    }
    // 65.10: a crate at each capture point, two people off its centre (inside the ring: standing there is holding it); it
    // serves whoever holds the point (no owner of its own) and starts stocked
    if (opts.pointSupplies !== false) {
      for (const site of state.modeState.points?.sites ?? []) {
        if (opts.droneDrops && (site.owner === null || site.owner === undefined)) continue;   // (69.32: with the drones, a point nobody holds stands bare till taken; one a side holds from the start has its crate, its own ground)
        if (!state.planets[site.planetIndex]) continue;
        const dir = pointCrateDir(state, site);
        extra.push({ id: state.structures.length + extra.length, type: 'supply', planetIndex: site.planetIndex, surfaceDir: dir, health: structureHealth('supply'), ownerId: -1, supply: { teamIndex: -1, site: site.id, bandages: SUPPLY.point.bandages, magazines: SUPPLY.point.magazines } });
      }
    }
    state = { ...state, structures: [...state.structures, ...extra] };
  }

  // I1 harness: information towers on a ring six person-heights out (a short walk at any scale), offset from the emplacements
  if (opts.towersPerTeam && opts.towersPerTeam > 0) {
    const teams = [...new Set(state.players.map(p => p.teamIndex))].sort((a, b) => a - b);
    const reach = 6 * PERSON_BODY_HEIGHT * personSetOf(state).k;
    const extra: StructureState[] = [];
    for (const t of teams) {
      const lead = state.players.filter(p => p.teamIndex === t).sort((x, y) => x.id - y.id)[0];
      const planet = lead ? state.planets[lead.planetIndex] : undefined;
      if (!lead || !planet) continue;
      for (let k = 0; k < opts.towersPerTeam; k++) {
        const ang = (k / opts.towersPerTeam) * Math.PI * 2 + 2.1;
        const n = vecNormalize(lead.surfaceDir);
        const ref = Math.abs(n.y) < 0.9 ? { x: 0, y: 1, z: 0 } : { x: 1, y: 0, z: 0 };
        const u = vecNormalize(vecCross(n, ref)), v = vecCross(n, u);
        const arc = reach / planet.radius;
        const dir = vecNormalize(vecAdd(vecScale(n, dcos(arc)), vecScale(vecAdd(vecScale(u, dcos(ang)), vecScale(v, dsin(ang))), dsin(arc))));
        extra.push({ id: state.structures.length + extra.length, type: 'tower', planetIndex: lead.planetIndex, surfaceDir: dir, health: structureHealth('tower'), ownerId: lead.id });
      }
    }
    state = { ...state, structures: [...state.structures, ...extra] };
  }

  // 67.11: at person scale the planet-scale rocks are gone (flat brown wedges twenty to fifty people tall; the rock fields
  // and mountain tors are the rocks at this scale) — before the stands, so their ground is free (classic: untouched)
  // 63.14 step 6: the settlements the board bounds, as neutral houses (core/settlements.ts) — before the dressing, so its stands keep off them and (§0.11 step B (3)) a patch stands at each; settled, terraced and seated with the rest at the end
  state = withSettlements(state);
  if (personSetOf(state).k < 1) state = dropGiantRocks(state);
  // E5 / decision 87: a person-scale battle is dressed with biome stands, after everything that claims ground (classic: untouched)
  if (opts.biomes !== false) {
    // §0.11 step B (2), with the region switch: the front's patches along the match's main routes (core/planetRoutes.ts mainRoutes: base to base and each base to each objective, on wheels)
    state = dressBattleSite(state, opts.seed, opts.standDensity ?? 1, opts.biomePatches !== false ? { patches: true, ...(opts.regionDressing ? { regions: true, routes: mainRoutes(state, pi => roadsOf(state, pi)).map(r => r.points) } : {}) } : {});   // 67.46 step 3: on unless told off; §0.11 step B (3): per region with the switch
    if (state.floraDestruction) state = { ...state, floraDestruction: initFloraDestructionState(state.flora) };
  }
  // 69.91 (a): the action clock's capture circles cleared of the map's rocks and flora whose walk footprint reaches in (story-2's
  // p0 stood under a tree no walk could get past) — after the dressing, before the forest (core/pointClearing.ts; classic: untouched)
  if (opts.clearPoints !== false) state = clearCapturePoints(state);
  // E5.4 step 4: the planet forest's planets and keep-out, once the battle is dressed (none held until the first tick)
  if (forestOn(opts) && !opts.noFlora) state = initForest(state);
  // (the landing tick: each planet's vertex cell index, which a crater's passes read, and its terrain index's triangle
  // bins, which a crater's bound-table patch reads, built here rather than on the match's first crater — caches:
  // nothing the match computes changes)
  for (const p of state.planets) { prepareVertexCells(p.basePositions); prepareSurfaceTables(p.basePositions); }
  if (opts.noFlora) {
    state = { ...state, flora: [] };
    if (state.floraDestruction) state = { ...state, floraDestruction: initFloraDestructionState(state.flora) };
  }

  if (opts.gunsByTeam?.some(w => w && Object.entries(w).some(([k, v]) => k !== 'rifle' && (v ?? 0) > 0))) state = dealLongGuns(state, opts.gunsByTeam, opts.seed);
  // 65.32: each member of a side with a layout gets its slot's primary and secondary (after the side's gun shares, which it overrides)
  if (layouts) state = issueLayouts(state, layouts, opts.squadTypes);
  // 69.45: with the support role on, each squad without a `support` slot names its last plain rifleman
  state = issueSupports(state);
  // 69.46: with the crewman's switch, each squad without a `crewman` slot names its first plain rifleman
  state = issueCrewmen(state);
  // 69.47 step 1: with the charges, each squad without an `engineer` slot names its first plain rifleman left
  state = issueEngineers(state);
  // 65.32: the player's own loadout, on its team's first unit only
  if (opts.playerLoadout?.weapons.length) {
    const id = favouredUnitId(state, opts.playerLoadout.team), w = opts.playerLoadout.weapons;
    if (id !== null) state = { ...state, players: state.players.map(p => (p.id === id ? applyCarry(p, w) : p)) };
  }
  // 185: the armour — a slot's own, else its side's; the player's own on its unit — a heavy vest's carry on the walk
  {
    const slots = layouts ? layoutSlots(state, layouts, opts.squadTypes) : undefined;
    const side = (t: number): ArmourIssue | undefined => armourIssueOf(opts.armourByTeam?.[t]);   // (190: helmets, heavyHelmets, light, heavy, heavyKit)
    const favoured = opts.playerLoadout?.armour ? favouredUnitId(state, opts.playerLoadout.team) : null;
    const issueFor = (p: PlayerState) => (p.id === favoured ? opts.playerLoadout!.armour : slots?.get(p.id)?.armour ?? side(p.teamIndex));
    if (state.players.some(p => armourOf(issueFor(p)))) {
      state = { ...state, config: { ...state.config, armour: true }, players: state.players.map(p => {
        const issue = issueFor(p), worn = armourOf(issue);
        if (!worn) return p;
        const carry = armourCarry(issue);
        return { ...p, armour: worn, ...(carry !== 1 && p.modifiers ? { modifiers: { ...p.modifiers, walkSpeedMult: (p.modifiers.walkSpeedMult ?? 1) * carry } } : {}) };
      }) };
    }
  }
  // 65.31: the 40 mm's rounds a life for each who carries one — its slot's count, else the setup's (absent both: the launcher's own)
  {
    const slotRounds = layouts ? layoutSlots(state, layouts, opts.squadTypes) : undefined;
    const issueOf = (p: PlayerState) => slotRounds?.get(p.id)?.gl40Rounds ?? opts.gl40Rounds;
    if (state.players.some(p => carriesGl40(p) && issueOf(p) !== undefined)) state = { ...state, players: state.players.map(p => { const n = issueOf(p); return carriesGl40(p) && n !== undefined ? issueGl40Whole({ ...p, gl40Issue: Math.max(0, Math.floor(n)) }) : p; }) };
  }
  if (opts.dronesPerUnit) state = { ...state, players: state.players.map(p => ({ ...p, drones: opts.dronesPerUnit })) };
  // 69.2b: each squad carries its relays (the build path pays from the squad's count)
  if (opts.relays && state.squads) state = { ...state, squads: state.squads.map(sq => ({ ...sq, relays: RELAY.carried })) };
  // 69.4: each side's forward bases to plant (with the web: relays on)
  if (opts.fobs) state = withFobs(state);
  // 125a: each squad's first unit carries the beacon (after the deploy, which rebuilds the players from the map's)
  if (opts.beacons && state.squads) {
    const carriers = new Set(state.squads.map(sq => Math.min(...sq.unitIds)));
    state = { ...state, players: state.players.map(p => (carriers.has(p.id) ? { ...p, beacons: BEACON.carried } : p)) };
  }
  // 69.4 step 6a: the side's build kits — the FOB count from its kit, the relays and beacons just issued drawn from theirs
  state = withBuildKits(state);
  // X20: a finite standard count where the sector gave one (an absent count reads as unlimited, as today)
  if (opts.ammoByTeam?.some(n => n !== undefined)) {
    state = { ...state, players: state.players.map(p => { const n = opts.ammoByTeam![p.teamIndex]; return n === undefined ? p : { ...p, loadout: { ...p.loadout, ammo: { ...p.loadout.ammo, standard: Math.max(0, Math.floor(n)) } } }; }) };
  }
  if (opts.dronesByTeam?.some(n => n > 0)) {
    const carry = new Map<number, number>();
    opts.dronesByTeam.forEach((n, t) => {
      const ids = state.players.filter(p => p.teamIndex === t).map(p => p.id).sort((a, b) => a - b);
      for (let i = 0; i < n && ids.length; i++) carry.set(ids[i % ids.length], (carry.get(ids[i % ids.length]) ?? 0) + 1);
    });
    state = { ...state, players: state.players.map(p => (carry.has(p.id) ? { ...p, drones: (p.drones ?? 0) + carry.get(p.id)! } : p)) };
  }
  // (65.32: a layout slot's grenades replace its side's issue for that member)
  const slotGrenades = layouts ? new Map([...layoutSlots(state, layouts, opts.squadTypes)].flatMap(([id, m]) => (m.grenades ? [[id, m.grenades] as const] : []))) : undefined;
  if (opts.grenades || slotGrenades?.size) {
    const g = opts.grenades;
    const forUnit = (p: PlayerState) => slotGrenades?.get(p.id) ?? (Array.isArray(g) ? g[p.teamIndex] : g);
    if (state.players.some(p => { const x = forUnit(p); return (x?.he ?? 0) + (x?.smoke ?? 0) > 0; })) {
      state = { ...state, players: state.players.map(p => { const x = forUnit(p); return x && (x.he ?? 0) + (x.smoke ?? 0) > 0 ? { ...p, grenades: { he: x.he ?? 0, smoke: x.smoke ?? 0 } } : p; }) };
      // 65.23: from the side's finite stock — each unit's issue kept, and each side's stock GRENADE_STOCK.refills issues a unit
      if (opts.grenadeStock) state = withGrenadeStocks(state);
    }
  }
  // 65.29 (2): rockets from the side's finite stock (the tube on), ROCKET_STOCK.refills issues a unit
  if (state.config.rocketStock) state = withRocketStocks(state);
  if (defaultOn(state.config, 'gl40Stock')) state = withGl40Stocks(state);   // 65.31 step 2
  state = withAirstrikes(state, opts.forceCompose ? sides.map(f => f.airstrikes) : airstrikesOf(opts));   // 68.39 (69.49: composed, each side's)
  state = withDropships(state, opts.forceCompose ? sides.map(f => f.dropships) : opts.dropshipsByTeam);   // 69.3b
  state = withGunships(state);   // 63.13
  state = withHelis(state);   // 69.59 step 1
  state = withHelipads(state);   // 69.74 step 2: each side's helicopters on its helipad, a spot each (with the switch)
  state = withPods(state, opts.forceCompose ? sides.map(f => f.pods) : opts.podsByTeam);   // DECISIONS 200 step 5
  // DECISIONS 207 (1) (the owner: "let's give bases an hq landing pad, and have dropships just idle there"): with wave ships,
  // a side with no landing pad gets one behind its people (the board's line), where its wave ship parks (withShipPads, below)
  if (opts.waveShips && state.config.clock === 'action') {
    const ps = personSetOf(state), person = PERSON_BODY_HEIGHT * ps.k, extra: StructureState[] = [];
    for (const t of [...new Set(state.players.map(p => p.teamIndex))].sort((a, b) => a - b)) {
      if (state.structures.some(s => s.type === 'landingPad' && state.players.find(p => p.id === s.ownerId)?.teamIndex === t)) continue;
      const lead = state.players.filter(p => p.teamIndex === t).sort((x, y) => x.id - y.id)[0], planet = lead ? state.planets[lead.planetIndex] : undefined;
      if (!lead || !planet) continue;
      const half = structureFootprint({ type: 'landingPad', builder: builderOf(t) }, state.config, planet.radius);
      const line = lineBehindTheTeam(state.players, t, lead.planetIndex, planet.radius, 1, 2 * half, half + ps.unitRadius + 2 * person);
      if (line) extra.push({ id: state.structures.length + extra.length, type: 'landingPad', planetIndex: lead.planetIndex, surfaceDir: line.spots[0], health: structureHealth('landingPad'), ownerId: lead.id });
    }
    state = { ...state, structures: [...state.structures, ...extra] };
  }
  // 69.30 step 1 (248 (1), (4)): each side's base crates — the team crates that hand out bandages, not the guns' shell dumps
  // nor a point's — hold the tank's rounds, a fixed amount
  // (step 3: the salvo's light shells are the base's one stock — on each side's first such crate only; the vehicles' rounds on each)
  if (opts.munitionsSupply) {
    const shelled = new Set<number>();
    const { lightShell, ...vehicles } = MUNITIONS.base;
    state = { ...state, structures: state.structures.map(s => {
      if (s.type !== 'supply' || !s.supply || s.supply.site !== undefined || s.supply.teamIndex < 0 || s.supply.bandages <= 0) return s;
      const first = !shelled.has(s.supply.teamIndex);
      shelled.add(s.supply.teamIndex);
      return { ...s, supply: { ...s.supply, ammo: first ? { ...vehicles, lightShell } : { ...vehicles } } };
    }) };
    // step 3: a side holding strikes has the salvos its base's light shells pay for (its count retired; the bots pace on it)
    const a = state.airstrikes;
    if (a) {
      const left: Record<number, number> = {};
      for (const t of Object.keys(a.left).map(Number)) left[t] = airstrikesLeft(state, t);
      state = { ...state, airstrikes: { ...a, left, issued: { ...left } } };
    }
  }
  // 65.19: payloads — each unit's charges and its issue (a crate tops drones and charges back up to it); the base crates stock both
  if (opts.chargesPerUnit !== undefined) {
    const per = opts.chargesPerUnit;
    const chargesFor = (t: number) => Math.max(0, Math.floor((Array.isArray(per) ? per[t] : per) ?? 0));
    state = { ...state, players: state.players.map(p => ({ ...p, charges: chargesFor(p.teamIndex), payloadIssue: { drones: p.drones ?? 0, charges: chargesFor(p.teamIndex) } })) };
    state = { ...state, structures: state.structures.map(s => (s.supply && s.supply.site === undefined && s.supply.teamIndex >= 0
      ? { ...s, supply: { ...s.supply, drones: DRONE_PAYLOADS.crateDrones, charges: DRONE_PAYLOADS.crateCharges } } : s)) };
  }
  if (opts.droneKind) {
    const kind = opts.droneKind;
    const strikeFor = (t: number) => (Array.isArray(kind) ? kind[t] : kind) === 'strike';
    if (state.players.some(p => strikeFor(p.teamIndex))) state = { ...state, players: state.players.map(p => (strikeFor(p.teamIndex) ? { ...p, droneKind: 'strike' as const } : p)) };
  }

  // Carried-in health (sector units arrive with what they left the last battle with)
  if (opts.healthByTeam) {
    const counters = new Map<number, number>();
    state = {
      ...state,
      players: state.players.map(p => {
        const i = counters.get(p.teamIndex) ?? 0;
        counters.set(p.teamIndex, i + 1);
        const h = opts.healthByTeam![p.teamIndex]?.[i];
        return h === undefined ? p : { ...p, health: Math.max(1, Math.min(100, h)) };
      }),
    };
  }

  // X2 at L2: the sector's marks land as tasks on the team's squads — a mark, or a fire mission — at the other side's landing
  if (opts.marks?.length && state.squads) {
    const planetIndex = state.players[0]?.planetIndex ?? 0;
    for (const m of opts.marks) {
      const facing = m.regionOf !== undefined ? opts.deployRegionByTeam?.[m.regionOf]?.facing : undefined;
      const other = state.players.filter(p => p.teamIndex !== m.team && p.alive);
      const at = facing ?? (other.length ? other[0].surfaceDir : { x: 0, y: 1, z: 0 });
      const pIdx = other.length && !facing ? other[0].planetIndex : planetIndex;
      const order: SquadOrder = m.fire ? { kind: 'fireMission', planetIndex: pIdx, surfaceDir: at, spread: 0 } : { kind: 'mark', planetIndex: pIdx, surfaceDir: at, turn: state.turnNumber, label: 'from the sector' };
      state = { ...state, squads: (state.squads ?? []).map(sq => (sq.team === m.team ? { ...sq, tasks: [...sq.tasks, { order, issuedTick: 0, by: 'setup' as const }] } : sq)) };   // (69.72 step 5, DECISIONS 304: the setup's, tagged)
    }
  }
  // X22: each team's exit, where it stands once every placement step is done (the arrival lane's facing where the sector gave one)
  // (69.6a: on the board, its base)
  if (opts.extraction || opts.reserves) {
    const facings: Array<Vec3 | undefined> = (opts.deployRegionByTeam ?? []).map(r => r?.facing);
    for (const b of state.board?.bases ?? []) facings[b.team] = b.dir;
    state = { ...state, exits: exitsFromStart(state.players, facings) };
  }
  // X19: the reserve is the ticket pool, and every wave is logged as an arrival
  if (opts.reserves) {
    const respawn = state.modeState.respawn ?? initRespawnState(state.players, 0);
    const tickets: Record<number, number> = { ...respawn.tickets };
    opts.reserves.forEach((n, t) => { tickets[t] = Math.max(0, Math.floor(n)); });
    state = { ...state, modeState: { ...state.modeState, respawn: { ...respawn, tickets } }, reserveArrivals: [] };
  }
  // R12 step 1: every entity names its body — exactly what the clock read it as, so nothing moves
  state = {
    ...state,
    players: state.players.map(p => p.body ? p : { ...p, body: unitBodyAtCreation(state) }),
    structures: state.structures.map(st => st.body ? st : { ...st, body: structureBodyKind(st) }),
  };
  state = withBuilders(state);   // 63.7 (b): whose building each planet-scale building is, where they are sized by function
  state = settleBuildings(state);   // and none stands on a point, a spawn, a vehicle or another structure (the coordinator, every-sk-2)
  state = planBases(state);   // the base planner: the base's structures clear of each other's reach — their decks and the skirts they grow on a slope (a shared terrace first, with the terraces on)
  state = layPoolTerraces(state);   // 69.14: each pool's terrace first (core/motorPoolStock.ts), so the base's decks are seated on the ground it leaves
  state = withLighterStrips(state);   // 69.10: each side's lighter strip behind its pool (with the switch) — 69.14: its terrace too, before the decks are seated (it buried an HQ's by 1.85 people, laid after)
  state = terraceBuilds(state);   // 63.14 2b: a terrace under each base whose footprint's rise asks for one (core/terraces.ts; through the terrain log)
  state = seatBuilds(state);   // 67.52: each person-scale build on its spot's fine ground (core/buildSeat.ts), where it now stands
  state = withStands(state);   // 209's follow-up: the walkable tops (the pads' decks) read as the person's ground (core/stands.ts)
  if (opts.droneDrops && state.config.clock === 'action') state = withSupplyDrones(state, opts.supplyDronesByTeam);   // 69.32: each side's supply drones
  if (opts.waveShips) state = withShipPads(state);   // DECISIONS 207 (1): each side's wave ship on its pad, where the pad now stands (209: on its deck)
  state = withVehicleStocks(state);   // 63.1n: each side's spare vehicles, and every vehicle's home where it now stands
  state = withMotorPools(state);   // 63.1n with 67.75: each stocked side's motor pool behind its base (with the switch)
  state = packStartFleet(state);   // 69.19: each side's start fleet — its unwrapped share stands, the rest packed in its pool's stock, queued to unpack
  state = planYards(state);   // 63.16 (h): the pools' compounds and the strips claim their ground; what stood under them moves out (core/basePlanner.ts)
  state = withConvoys(state);   // 69.4 step 5: each side's base stock of loads (with convoys)
  if (state.config.storyObjectives && state.config.clock === 'action') state = withStoryObjectives(state);   // 69.50 step 1: from the setup, every structure placed
  state = toughenTargets(state);   // 69.47 step 1: with the charges, the destroy objectives' targets toughened
  if (state.config.intelRecord && state.config.clock === 'action') state = seedBaseIntel(state);   // 69.73 step 3: what each side knows of the other's base, every structure placed
  // 69.6d (the owner, playing: "play should spawn facing objectives"): on the action clock everyone starts facing the
  // nearest objective of interest to its side (core/capturePoints.ts facingObjective, the respawns' rule), where nothing
  // placed it facing already
  if (state.config.clock === 'action') {
    const at = state;
    state = { ...state, players: state.players.map(p => (p.heading ? p : { ...p, ...(facingObjective(at, p.teamIndex, p) ? { heading: facingObjective(at, p.teamIndex, p) } : {}) })) };
  }
  return { state: opts.prepareState ? opts.prepareState(state) : state, difficulty };
}

/**
 * What every match needs on top of its map, units and mode layers — the same for a
 * menu match (Game.startGame) and a setup (createMatchState): flora destruction
 * tracking, the match's random events, and the turn order.
 */
export function completeMatchState(state: GameState): GameState {
  // R12 step 1: every unit and structure names its body (the browser's setup comes through here too)
  state = {
    ...state,
    players: state.players.map(p => p.body ? p : { ...p, body: unitBodyAtCreation(state) }),
    structures: state.structures.map(st => st.body ? st : { ...st, body: structureBodyKind(st) }),
  };
  state = withBuilders(state);   // 63.7 (b): whose building each planet-scale building is, where they are sized by function
  let s = state;
  // 57.4: with the alert layer on and no guard teams named, the bot teams guard (the human is the infiltrator)
  if (s.config.modeConfig.startingAlert && s.config.modeConfig.guardTeams === undefined) {
    s = { ...s, config: { ...s.config, modeConfig: { ...s.config.modeConfig, guardTeams: botGuardTeams(s.players) } } };
  }
  if (s.flora && s.flora.length > 0) s = { ...s, floraDestruction: initFloraDestructionState(s.flora) };
  if (s.config.modeConfig.eventsEnabled !== false) {
    s = { ...s, matchEvents: initMatchEventState(s.config.modeConfig, new SeededRandom(s.config.seed + '-events')) };
  }
  return { ...s, config: { ...s.config, turnOrder: computeTurnOrder(s.players, s.config.turnOrderMode ?? 'sequential') } };
}

/**
 * A Match ready to run from a setup: its state (createMatchState), turn flow, and true
 * planet colours (blank — a headless match draws nothing; craters recolour them).
 */
export function createMatch(setup: MatchSetup): { match: Match; difficulty: string[] } {
  const { state, difficulty } = createMatchState(setup);
  const match = matchFromState(state);
  // R11 step 2: the setup's first inputs, logged at tick 0 from the setup's own seat (-1000: no team)
  for (const body of setup.firstInputs ?? []) match.apply(match.stamp(SETUP_ACTOR, body));
  return { match, difficulty };
}

/** The actor id of a setup's own inputs (the fidelity switch that opened a battle): no seat, no unit. */
export const SETUP_ACTOR = -1000;

/** A Match around a ready state (no rendering: blank true colours). */
export function matchFromState(state: GameState): Match {
  const match = new Match();
  match.state = state;
  match.turnFlow = state.config.turnFlow ?? 'perUnit';
  for (const p of state.planets) match.truePlanetColors.set(p.id, new Float32Array(p.heightmap.length * 3));
  return match;
}
